import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useLocation } from 'react-router-dom';
import './styles.scss';

import { BubbleMenu, EditorProvider, useCurrentEditor } from '@tiptap/react';
import { getHospitalId, getRole, getUserId, getName } from '../../../../platform/custom/Utils/authUtils';
import {
  canEditMedicalData,
  canEngineWriteToReport,
  isAssignedDoctor,
  isDoctorRole,
} from '../../Utils/reportPermissions';

import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createReportApi } from '../../rtk-apis/reports/createReport';
import { clearState as clearCreateState } from '../../store/slices/reports/createReport';
import { extensions } from '../../Utils/reportExtension';
import { useClinicDropdowns } from '../../Utils/useClinicDropdowns';
import {
  NUMERIC_REPORT_FIELDS,
  NUMERIC_EMPTY_MARKER,
  isNumericFieldKey,
  numericAliasGroup,
  wrapNumericField,
  isCompleteNumericEntry,
} from './numericReportFields';
import { CHAIN_FIELDS, recomputeChain } from '../../Utils/measurementChain';
import { wrapChainField } from '../../Utils/chainFieldMarkup';
import {
  applyChainUpdates,
  collectChainSnapshot,
  findEditedChainValue,
} from '../../Utils/chainFieldSync';
import { backfillChainMarks } from '../../Utils/chainFieldBackfill';
import { applyDynamicFieldUpdate } from '../../Utils/dynamicFieldSync';
import {
  CHAIN_POPUP_HEIGHT,
  CHAIN_POPUP_WIDTH,
  NUMERIC_POPUP_HEIGHT,
  NUMERIC_POPUP_WIDTH,
  SUGGESTION_POPUP_HEIGHT,
  SUGGESTION_POPUP_WIDTH,
  getReportPopupPosition,
} from './components/reportPopupPosition';
import { backfillAortaSlot } from '../../Utils/aortaSlot';
import {
  applyRapFromPhrase,
  backfillTricuspidPressure,
  isRapPhrasePosition,
  syncRapPhrase,
} from '../../Utils/tricuspidPressure';
import { stripChainMarks } from '../../Utils/chainDocSanitize';
import {
  resolveUnit,
  splitValueAndUnit,
  joinValueAndUnit,
  isCompleteNumber,
  CHAIN_EMPTY_MARKER,
} from '../../Utils/measurementValueParts';
import { evaluateWordingRules } from '../../Utils/wordingRules';
import { resolvePhrase } from '../../Utils/wordingVocabulary';
import {
  applyPhraseUpdates,
  collectPhraseSnapshot,
  groupPhraseOccurrences,
  isPhraseSlotLocked,
  markPhraseUserOwned,
} from '../../Utils/phraseSlotSync';
import {
  ARRHYTHMIA_SLOT,
  resolveArrhythmiaPhrase,
  type DetectedArrhythmia,
} from '../../Utils/arrhythmiaVocabulary';
import { PEAK_HR_KEY, deriveStressHeartRateValues } from '../../Utils/stressHeartRate';
import { applyNumericUpdates } from '../../Utils/numericFieldSync';
import { OHIFCornerstoneSRContainer } from '../../../../extensions/cornerstone-dicom-sr/src/components/OHIFCornerstoneSRContainer';

import {
  fetchPatientDetails,
  extractPatientDemographics,
  convertMeasurementsToHTML,
  extractSeriesInstanceUID,
  getSeriesMetadata,
  resolveReportBSA,
  resolveReportHeartRate,
  extractBloodPressureFromContainer,
  getMachineFromStudyTags,
  extractContainerData as extractContainerDataShared,
  findSRValueByExactConceptName,
} from './helper';
import { Button, Icon } from '@ohif/ui';
import { formatDateToDDMMYYYY } from '../../../custom/Utils/utils';

import { RestBullsEyePlot } from './components/RestBullsEyePlot';
import { StressBullsEyePlot } from './components/StressBullsEyePlot';
import { TargetCodes } from './components/TargetCodes';
import { Apical2Ch } from './components/Apical2Ch';
import { Apical3Ch } from './components/Apical3Ch';
import { Apical4Ch } from './components/Apical4Ch';

import { StressApical2Ch } from './components/StressApical2Ch';
import { StressApical3Ch } from './components/StressApical3Ch';
import { StressApical4Ch } from './components/StressApical4Ch';
import { updateReport } from '../../rtk-apis/reports/updateReport';
import { getStressValues } from '../../rtk-apis/reports/getStressValues';
import { saveDiagramHtml } from '../../rtk-apis/reports/saveDiagramHtml';
import { getCurrentReportApi } from '../../rtk-apis/reports/getCurrentReport';
import { getStudyMarkingsApi } from '../../rtk-apis/reports/getStudyMarkingsApi';
import AttachClipsModal from './components/AttachClipsModal';
import { useAutoAttachClips } from './components/useAutoAttachClips';
import AssignDoctorModal from '../patients/components/assignToModal';
import AssignSonographerModal from '../patients/components/assignSonographerModal';
import { clearState as clearUpdateState } from '../../store/slices/reports/updateReport';
import { Failed, Loader, Success } from '../../../custom/components';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import useDebounce from '../../components/useDebounce';
import { captureDiagramHtml } from '../../Utils/captureDiagramHtml';

/** How long the chain / wording notice stays up before clearing itself. */
const CHAIN_NOTICE_AUTO_DISMISS_MS = 7000;

interface ReportLayoutProps {
  closeReportPanel?: any;
  reportTemplate?: any;
  headerDetails?: any;
  ref?: any;
  setIsLoading?: any;
  setIsSuccess?: any;
  setSRInstance?: any;
  SRInstance?: any;
  /** False while the study's image series are still loading (Express). */
  imageMetadataReady?: boolean;
  setUpdatedEditorContent?: any;
  colorCode?: any;
  onReportCreated?: (reportId: string, savedContent: any) => void;
  onAutoSaveStatusChange?: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
  attachedClips?: any[];
  reportAssignedTo?: string;
  /**
   * Bumped once per successful assignment via the Assign Cardiologist modal.
   * A change in this value — not a change in the name — is what tells the
   * editor to write the cardiologist into an already-saved report body.
   */
  assignmentSeq?: number;
  onAssignCardiologist?: () => void;
  /** Sonographer of record, when one has been chosen for this report. */
  sonographer?: string;
  onSelectSonographer?: () => void;
  /** True only for a brand-new report: draft it on open (no Save Report button). */
  autoDraft?: boolean;
}

export const MenuBar = ({ editor }) => {
  return (
    <div className="control-group">
      <BubbleMenu editor={editor}>
        <div className="bubble-menu">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
          >
            <strong>
              <em>I</em>
            </strong>
          </button>
          <button
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={editor.isActive('paragraph') ? 'is-active' : ''}
          >
            P
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          >
            <strong>H1</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          >
            <strong>H2</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          >
            <strong>H3</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
            className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}
          >
            <strong>H4</strong>
          </button>
        </div>
      </BubbleMenu>
    </div>
  );
};

/**
 * ReportLayout component is responsible for rendering the report editor layout.
 * It utilizes the current editor instance to populate a template with data and
 * manages the editor state to reflect the content.
 *
 * Props:
 * - closeReportPanel: A function to close the report panel.
 * - reportTemplate: A template string that is populated with data and set into the editor.
 *
 * This component:
 * - Populates the provided report template with data upon mounting.
 * - Updates the editor content once the template is populated.
 * - Provides a save button to log the current editor content.
 * - Includes a BubbleMenu with formatting options like bold, italic, and headings.
 */
const ReportLayout: React.FC<ReportLayoutProps> = forwardRef((props, ref) => {
  const { editor } = useCurrentEditor();
  const { dropdownMap } = useClinicDropdowns();
  const [content, setContent] = useState('');
  const [editorContent, setEditorContent] = useState<any>(null);
  const [patientDemographics, setPatientDemographics] = useState<any>({});
  const [measurementsHTML, setMeasurementsHTML] = useState(null);
  const [data, setData] = useState<any>({});
  // CardioSoft stress values ({{stress_*}} placeholders) parsed from the study's
  // attached XML; missing values fall back to "--" so the template still shows a
  // fill-me marker.
  const [stressData, setStressData] = useState<Record<string, string>>({});
  /**
   * Which attachment filled the {{stress_*}} values, and in what format.
   *
   * Worth surfacing rather than hiding: a value read from the structured XML
   * export and the same value scraped from the printed PDF are not equally
   * trustworthy, and neither is the same as one the clinician typed. Shown in
   * the numeric edit popup — the moment someone is actually looking at the
   * number and deciding whether to accept it.
   */
  const [stressSource, setStressSource] = useState<{ format: string; file: string } | null>(null);
  /**
   * The arrhythmia the CardioSoft export recorded, if it recorded one.
   *
   * `null` covers two different things and deliberately does not distinguish
   * them here — no stress export attached, and an export that said nothing about
   * rhythm. Both mean the same to the report: leave the sentence alone.
   */
  const [stressArrhythmia, setStressArrhythmia] = useState<DetectedArrhythmia | null>(null);
  const stressValuesResponse = useSelector((state: RootState) => state.getStressValuesResponse);
  const [seriesInstanceUID, setSeriesInstanceUID] = useState<any>(null);
  const [SRInstanceData, setSRInstanceData] = useState(null);
  const [htmlString, setHtmlString] = useState('');
  /**
   * This study's flat SR content items, set whenever the SR is (re)parsed below.
   * Kept in a ref (not state) so the big array itself never drives a re-render —
   * only `srReadyTick` below does, and only once, when it actually changes.
   */
  const srContentItemsRef = React.useRef<any[] | null>(null);
  /**
   * Bumped every time `srContentItemsRef` is (re)written. The SR is parsed inside
   * an async fetch (`handleFetchDemographics` below), which resolves AFTER the
   * dynamic-field-catch effect's own [editor, htmlString] deps have already
   * settled — a plain ref write doesn't re-run an effect, so without this tick the
   * catch effect could run once, find `srContentItemsRef.current` still null, and
   * never get a second chance once the SR data actually lands.
   */
  const [srReadyTick, setSrReadyTick] = useState(0);
  /** A report loads its SR once; this stops the catch from re-running on every unrelated re-render. */
  const srConceptCatchAppliedRef = React.useRef(false);

  const [suggestions, setSuggestions] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);
  // Numeric slot editing — the typed-value counterpart to the phrase dropdown
  // above. Held separately so the two popups can never be open at once.
  const [numericSlot, setNumericSlot] = useState<{
    from: number;
    to: number;
    key: string;
  } | null>(null);
  const [numericPosition, setNumericPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const [numericDraft, setNumericDraft] = useState('');
  // Escape blurs the input as it closes the popup; without this the blur handler would
  // commit the value the clinician just discarded.
  const numericCancelledRef = React.useRef(false);
  React.useEffect(() => {
    numericCancelledRef.current = false;
  }, [numericSlot?.key, numericSlot?.from]);
  /**
   * Field chaining (Smart Reporting priority #1, 25 Aug call) — the chained
   * measurement value currently being edited. `originalValue` is kept because
   * the chain needs the pre-edit number to rescale the BSA-indexed values
   * whose numerator never prints on the report.
   */
  const [chainSlot, setChainSlot] = useState<{
    from: number;
    to: number;
    key: string;
    label: string;
    /** The whole printed value ("4.8 cm") — what a no-change commit compares against. */
    originalValue: string;
    /** Fixed unit, shown beside the input and reattached on commit. Never edited. */
    unit: string;
    /** The value's own number/unit separator, reused so an unedited value is unchanged. */
    separator: string;
  } | null>(null);
  const [chainPosition, setChainPosition] = useState<{ top: number; left: number } | null>(null);
  const [chainDraft, setChainDraft] = useState('');
  /**
   * Dynamic fields (17 Sep, "dynamic fields" ask) — a clinic-created synced field,
   * carrying the `dynamicField` Mark instead of `chainField`. Deliberately its own
   * state and popup rather than folded into `chainSlot`: a dynamic field has no
   * unit, no CHAIN_FIELDS entry, and always freeform text, so it needs none of
   * chainSlot's unit/separator bookkeeping.
   */
  const [dynamicFieldSlot, setDynamicFieldSlot] = useState<{
    from: number;
    to: number;
    key: string;
    label: string;
    originalValue: string;
    /** 18 Sep — 'text' (v1 default), 'dropdown', or 'number'. */
    fieldType: 'text' | 'dropdown' | 'number';
    options: string[];
    unit: string;
  } | null>(null);
  const [dynamicFieldPosition, setDynamicFieldPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [dynamicFieldDraft, setDynamicFieldDraft] = useState('');
  const dynamicFieldCancelledRef = React.useRef(false);
  React.useEffect(() => {
    dynamicFieldCancelledRef.current = false;
  }, [dynamicFieldSlot?.key, dynamicFieldSlot?.from]);
  // Escape blurs the input as it closes the popup; without this the blur would commit the
  // value the clinician just discarded.
  const chainCancelledRef = React.useRef(false);
  React.useEffect(() => {
    chainCancelledRef.current = false;
  }, [chainSlot?.key, chainSlot?.from]);
  /**
   * What the last commit changed, shown briefly under the report so the
   * clinician can see the chain ran rather than hunting the table for moved
   * numbers. Cleared on the next edit.
   */
  const [chainNotice, setChainNotice] = useState<{
    changed: string[];
    warnings: string[];
  } | null>(null);
  /**
   * The notice clears itself after a few seconds.
   *
   * Dr Shashi, 06 Sep: *"Every time we have to dismiss. Rather then saving time it's
   * causing more time consumption"*. A notice reporting something already done should not
   * demand a click before the report can be read; the Dismiss link stays for anyone who
   * wants it gone sooner.
   *
   * ⚠️ Keyed on WHETHER a notice is showing, never on WHAT it says. The first attempt
   * depended on the notice's text, and Kevin's test found it never cleared at all: the
   * chain re-announces on a 600 ms debounce, every re-announcement changed the dependency,
   * the cleanup cancelled the pending timeout and armed a fresh one — so the countdown was
   * pushed back faster than it could ever elapse. A boolean dependency cannot be reset by
   * churn; the clock starts when the notice appears and runs to the end regardless of how
   * many times the engine repeats itself in the meantime.
   */
  const chainNoticeVisible = chainNotice !== null;
  React.useEffect(() => {
    if (!chainNoticeVisible) {
      return undefined;
    }
    const timer = setTimeout(() => setChainNotice(null), CHAIN_NOTICE_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [chainNoticeVisible]);
  /**
   * Last known `key -> value` snapshot of the document's chained values. The
   * typing-driven chain works by diffing against this: whatever moved is what
   * the clinician just edited.
   */
  const chainValuesRef = React.useRef<Record<string, string> | null>(null);
  /**
   * True while the chain itself is writing to the document — marking values on
   * open, or applying recomputed ones. Keeps those writes out of the dirty
   * flag and out of autosave, so merely OPENING a report never saves it.
   */
  const chainWritingRef = React.useRef(false);
  /**
   * Phrases as they stood when the report was opened, plus anything the wording rules
   * have written since. A slot is only auto-filled while it still matches one of those —
   * once a clinician changes it, the rule flags a disagreement instead of overwriting.
   */
  /**
   * Ownership of a phrase slot now lives in the DOCUMENT (`data-user` / `data-auto` on the
   * span), not in a ref. A ref cannot survive a save, which is exactly how a tech's
   * correction came back the next day looking like a template default and got overwritten
   * — see `isPhraseSlotLocked` and Dr Shashi's report of 06 Sep.
   */
  const [bsaSR, setBSASR] = useState(null);
  // Resolved BSA (m², numeric) — SR value if present, else calculated from
  // height/weight. Feeds the demographics header so BSA shows on every report.
  const [bsaCalc, setBsaCalc] = useState<number | null>(null);
  // Heart rate (display string, e.g. "72 bpm") parsed from the SR. Feeds the
  // {{heart_rate}} placeholder in the demographics header.
  const [heartRate, setHeartRate] = useState<string | null>(null);
  // {{blood_pressure}} from the SR ("120/80"); blank → clinician types it.
  const [bloodPressure, setBloodPressure] = useState<string | null>(null);
  // {{machine_type}} — "Manufacturer Model" from the image tags.
  const [machineType, setMachineType] = useState<string | null>(null);
  // Whether this study carries the AF mark. Changes how EF Biplane is resolved
  // (average of every biplane trace rather than the last one), so measurement
  // extraction re-runs when it resolves.
  const [isAF, setIsAF] = useState(false);
  const onAssignCardiologistRef = React.useRef(props?.onAssignCardiologist);
  onAssignCardiologistRef.current = props?.onAssignCardiologist;
  const onSelectSonographerRef = React.useRef(props?.onSelectSonographer);
  onSelectSonographerRef.current = props?.onSelectSonographer;

  /**
   * The report's own status, for the write guards below. A Finalized report is locked for
   * everyone, and that has to hold for the engine's programmatic writes as well as for a
   * clinician's clicks.
   */
  const reportStatus = props?.reportTemplate?.data?.status;
  const reportStatusRef = React.useRef(reportStatus);
  reportStatusRef.current = reportStatus;
  const dropdownMapRef = React.useRef(dropdownMap);
  dropdownMapRef.current = dropdownMap;
  /**
   * Held in a ref for the same reason the dropdown lists are: the wording pass is
   * triggered by the document and the measurements changing, and reading this
   * through a ref keeps a newly arrived stress export from being a dependency of
   * every callback that touches wording.
   */
  const stressArrhythmiaRef = React.useRef(stressArrhythmia);
  stressArrhythmiaRef.current = stressArrhythmia;

  if (!editor) {
    return null;
  }

  // ------------------------------------------------------------------------------------------------------------ //

  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleClick = event => {
      // Detect click on Cardiologist value cell → open assign modal
      const clickedTd = event.target.closest('td');
      if (clickedTd && onAssignCardiologistRef.current) {
        const isMarked = clickedTd.hasAttribute('data-assign-cardiologist');
        const prevTd = clickedTd.previousElementSibling;
        const isPrevCardiologist = prevTd && /cardiologist:/i.test(prevTd.textContent?.trim() || '');
        // On a brand-new report the value cell is empty and barely clickable, so the
        // "Cardiologist:" label itself opens the picker as well.
        const isLabelCell = /cardiologist:/i.test(clickedTd.textContent?.trim() || '');
        if (isMarked || isPrevCardiologist || isLabelCell) {
          onAssignCardiologistRef.current();
          return;
        }
      }

      // Same affordance for the Sonographer row (client request, 26 Aug): the value cell
      // or the label opens the sonographer picker.
      if (clickedTd && onSelectSonographerRef.current) {
        const prevTd = clickedTd.previousElementSibling;
        const isPrevSono = prevTd && /sonographer:/i.test(prevTd.textContent?.trim() || '');
        const isSonoLabel = /sonographer:/i.test(clickedTd.textContent?.trim() || '');
        if (isPrevSono || isSonoLabel) {
          onSelectSonographerRef.current();
          return;
        }
      }

      // Block suggestions for non-editable roles
      const role = getRole();
      if (!canEngineWriteToReport(role, reportStatusRef.current)) return;

      // Chained measurement value — click to retype it; committing pushes the
      // change through everything derived from it.
      const chainTarget = event.target.closest('span[data-chain-field]');
      if (chainTarget) {
        event.stopPropagation();
        const chainKey = chainTarget.classList[0];
        if (!chainKey || !CHAIN_FIELDS[chainKey]) {
          return;
        }
        const chainPos = editor.view.posAtDOM(chainTarget, 0);
        const currentChainValue = chainTarget.textContent || '';
        setSuggestions([]);
        setDropdownPosition(null);
        setNumericPosition(null);
        setNumericSlot(null);
        setChainNotice(null);
        // Only the figure is offered for editing; the unit is held aside and put back
        // on commit, so it cannot be mistyped or dropped.
        const chainParts = splitValueAndUnit(currentChainValue);
        // Freeform fields (EF_Prose) have no unit to split off, and splitting one out
        // anyway would truncate anything that happens to start with a digit — "45-60%,
        // mildly reduced" would otherwise open the popup showing only "45". The whole
        // text is the editable draft; a placeholder still opens empty.
        const isFreeformChainField = !!CHAIN_FIELDS[chainKey]?.freeform;
        const chainNumber =
          isFreeformChainField && currentChainValue.trim() !== CHAIN_EMPTY_MARKER
            ? currentChainValue.trim()
            : chainParts.number;
        // An empty cell has no unit of its own — take the field's, or the first value
        // typed into it is written bare.
        //
        // Freeform fields have no unit at all, ever — `resolveUnit` must not run for
        // them. It reads `chainParts.unit`, which comes from `splitValueAndUnit` treating
        // the WHOLE text as "<number><rest>": a value like "40-50%" starts with digits,
        // so it split into number "40" and unit "-50%" and the popup would show a phantom
        // "-50%" label beside the box — and worse, re-append it on commit if the typed
        // draft ever stopped ending in "-50%" ("60-70%" → "60-70%-50%").
        const { unit: chainUnit, separator: chainSeparator } = isFreeformChainField
          ? { unit: '', separator: '' }
          : resolveUnit(chainParts, CHAIN_FIELDS[chainKey]?.unit);
        setChainDraft(chainNumber);
        setChainSlot({
          from: chainPos,
          to: chainPos + currentChainValue.length,
          key: chainKey,
          label: CHAIN_FIELDS[chainKey].label,
          // Rebuilt from the parts rather than the raw DOM text: EF's mark holds the figure
          // only, so `currentChainValue` is unit-less and would never equal a rejoined
          // "unchanged" commit, marking the report dirty for a field nobody touched.
          originalValue: joinValueAndUnit(chainNumber, chainUnit, chainSeparator),
          unit: chainUnit,
          separator: chainSeparator,
        });
        const reportElement = editor.view.dom.closest('.report-layout') as HTMLElement | null;
        setChainPosition(
          getReportPopupPosition(
            chainTarget,
            reportElement,
            CHAIN_POPUP_WIDTH,
            CHAIN_POPUP_HEIGHT
          )
        );
        return;
      }

      // Dynamic field (clinic-created, 17 Sep) — always freeform text, no unit.
      const dynamicFieldTarget = event.target.closest('span[data-dynamic-field]');
      if (dynamicFieldTarget) {
        event.stopPropagation();
        const dynamicKey = dynamicFieldTarget.classList[0];
        if (!dynamicKey) {
          return;
        }
        const dynamicPos = editor.view.posAtDOM(dynamicFieldTarget, 0);
        const currentValue = dynamicFieldTarget.textContent || '';
        const dynamicFieldType =
          (dynamicFieldTarget.getAttribute('data-field-type') as 'text' | 'dropdown' | 'number' | null) ||
          'text';
        const dynamicUnit = dynamicFieldTarget.getAttribute('data-unit') || '';
        let dynamicOptions: string[] = [];
        const rawOptions = dynamicFieldTarget.getAttribute('data-options');
        if (rawOptions) {
          try {
            dynamicOptions = JSON.parse(rawOptions);
          } catch {
            dynamicOptions = [];
          }
        }
        const dynamicLabel = dynamicFieldTarget.getAttribute('title') || dynamicKey;
        // `wrapDynamicField` prints "[Label]" for a field with no value yet (see
        // dynamicFieldMarkup.ts) — that bracketed text is a placeholder, not a real
        // value, and must not show up as a selectable option or a starting number.
        const isUnfilledPlaceholder = currentValue === `[${dynamicLabel}]`;
        // Number fields never show the unit in the input — same reasoning as
        // chainField's own popup: the unit is fixed furniture, reattached on commit.
        const dynamicDraftValue = isUnfilledPlaceholder
          ? ''
          : dynamicFieldType === 'number'
            ? splitValueAndUnit(currentValue).number
            : currentValue;
        setSuggestions([]);
        setDropdownPosition(null);
        setNumericPosition(null);
        setNumericSlot(null);
        setChainNotice(null);
        setDynamicFieldDraft(dynamicDraftValue);
        setDynamicFieldSlot({
          from: dynamicPos,
          to: dynamicPos + currentValue.length,
          key: dynamicKey,
          label: dynamicLabel,
          originalValue: currentValue,
          fieldType: dynamicFieldType,
          options: dynamicOptions,
          unit: dynamicUnit,
        });
        const reportElementForDynamic = editor.view.dom.closest('.report-layout') as HTMLElement | null;
        // A dropdown with options opens the same list as the template's own fields.
        const opensAsList = dynamicFieldType === 'dropdown' && dynamicOptions.length > 0;
        setDynamicFieldPosition(
          getReportPopupPosition(
            dynamicFieldTarget,
            reportElementForDynamic,
            opensAsList ? SUGGESTION_POPUP_WIDTH : CHAIN_POPUP_WIDTH,
            opensAsList ? SUGGESTION_POPUP_HEIGHT : CHAIN_POPUP_HEIGHT
          )
        );
        return;
      }

      // Numeric slot — no vocabulary to offer, so it opens a typed-value editor.
      const numericTarget = event.target.closest('span[data-editable-number]');
      if (numericTarget) {
        event.stopPropagation();
        const numericKey = numericTarget.classList[0];
        if (!numericKey || !isNumericFieldKey(numericKey)) {
          return;
        }
        const numPos = editor.view.posAtDOM(numericTarget, 0);
        const current = numericTarget.textContent || '';
        setSuggestions([]);
        setDropdownPosition(null);
        setNumericDraft(current === NUMERIC_EMPTY_MARKER ? '' : current);
        setNumericSlot({
          from: numPos,
          to: numPos + current.length,
          key: numericKey,
        });
        const reportElement = editor.view.dom.closest('.report-layout') as HTMLElement | null;
        setNumericPosition(
          getReportPopupPosition(
            numericTarget,
            reportElement,
            NUMERIC_POPUP_WIDTH,
            NUMERIC_POPUP_HEIGHT
          )
        );
        return;
      }

      const target = event.target.closest('span[data-editable]');
      if (!target) {
        return;
      }

      event.stopPropagation(); // Prevent event bubbling issues
      // A phrase slot, a numeric popup and a chain popup must never be open together.
      setNumericPosition(null);
      setNumericSlot(null);
      setChainPosition(null);
      setChainSlot(null);

      const spanClass = target.classList[0];
      if (!spanClass) {
        return;
      }

      const pos = editor.view.posAtDOM(target, 0);
      const options = dropdownMapRef.current[spanClass] || [];
      setSuggestions(options);
      setSelectedSpan({ from: pos, to: pos + target.textContent.length, className: spanClass });

      const reportElement = editor.view.dom.closest('.report-layout') as HTMLElement | null;
      setDropdownPosition(
        getReportPopupPosition(
          target,
          reportElement,
          SUGGESTION_POPUP_WIDTH,
          SUGGESTION_POPUP_HEIGHT
        )
      );
    };

    editor.view.dom.addEventListener('click', handleClick);
    return () => editor.view.dom.removeEventListener('click', handleClick);
  }, [editor]);

  const handleReplaceWord = suggestion => {
    const role = getRole();
    if (!canEngineWriteToReport(role, reportStatus)) return;
    if (editor && selectedSpan) {
      const scrollPosition = editor.view.dom.scrollTop; // Save current scroll position
      editor
        .chain()
        .deleteRange({ from: selectedSpan.from, to: selectedSpan.to })
        .insertContentAt(
          selectedSpan.from,
          `<span data-editable class='${selectedSpan.className}'>${suggestion}</span>`
        )
        .setTextSelection(selectedSpan.from + suggestion.length) // Set cursor after the new text
        .run();

      // Every OTHER copy of the same sentence. `la-no` prints twice in 29 of the
      // 32 ECHO templates and `physiological` in 27, so picking a word in the
      // Findings used to leave the Conclusions copy still reading the old one —
      // the same complaint Nikhil raised about the numeric fields ("if they're
      // using the same thing up there and up there, if you change one of them,
      // the other should change", 02 Sep).
      //
      // Only the copies describing the SAME finding. The Aortic Valve line prints
      // `la-no` twice, before "aortic regurgitation" and before "aortic stenosis", so
      // matching on the class alone answered both from one pick. Passing the clicked
      // position narrows it to the copies that qualify the same words.
      //
      // Marked as chain writing for the duration: the propagated copies are
      // housekeeping on the clinician's own pick, and letting the editor's
      // `update` handler fire between the two writes would push a half-updated
      // document out for autosave.
      chainWritingRef.current = true;
      try {
        applyPhraseUpdates(
          editor,
          { [selectedSpan.className]: suggestion },
          false,
          selectedSpan.from
        );
        // Their choice, recorded in the document so it survives the save. The engine
        // will not write to this sentence again on this report.
        markPhraseUserOwned(editor, selectedSpan.className, selectedSpan.from);
        // Right atrial pressure is picked from a dropdown but it is also a measurement, so
        // the chain writes it: RAP into the table, RVSP recomputed from it and the
        // gradient. Base values layered under the document's, as the commit paths do it —
        // a gradient measured but never printed is otherwise invisible and RVSP would
        // quietly fail to move.
        if (isRapPhrasePosition(editor, selectedSpan.from)) {
          const rapResult = applyRapFromPhrase(editor, suggestion, {
            ...data,
            ...(data?.measurements || {}),
          });
          chainValuesRef.current = collectChainSnapshot(editor);
          if (rapResult.changed.length > 0 || rapResult.warnings.length > 0) {
            setChainNotice({ changed: rapResult.changed, warnings: rapResult.warnings });
          }
        }
      } finally {
        chainWritingRef.current = false;
      }

      // The pick and its copies DO belong in the saved report — this is the
      // clinician's edit, propagated — so push the result out for autosave.
      const updatedContent = editor.getJSON();
      setEditorContent(updatedContent);
      props?.setUpdatedEditorContent(stripChainMarks(updatedContent));

      // Restore scroll position
      setTimeout(() => {
        editor.view.dom.scrollTop = scrollPosition;
      }, 0);

      setDropdownPosition(null);
    }
  };
  /**
   * Write a typed value back into its numeric slot.
   *
   * Clearing the field restores the "--" marker rather than deleting the span:
   * an empty span would collapse and leave the clinician nothing to click, so
   * the slot would be lost from the report for good.
   *
   * Typing the peak heart rate additionally recalculates the percentage of
   * age-predicted maximum HR and writes it into EVERY occurrence — the Stress
   * Findings sentence and the Conclusions bullet both print it. That is
   * Dr Shashi's request of 31 Aug: "sono usually inputs peak HR hence, system
   * automatically calculates the percentage. This is done on both conclusion and
   * body." See `stressHeartRate.ts` for the formula and for why an
   * underivable value leaves the report untouched.
   */
  const handleCommitNumeric = () => {
    const role = getRole();
    if (!canEngineWriteToReport(role, reportStatus)) {
      return;
    }
    if (!editor || !numericSlot) {
      return;
    }

    const trimmed = numericDraft.trim();
    const nextValue = trimmed || NUMERIC_EMPTY_MARKER;
    const committedKey = numericSlot.key;
    const scrollPosition = editor.view.dom.scrollTop;
    editor
      .chain()
      .deleteRange({ from: numericSlot.from, to: numericSlot.to })
      .insertContentAt(numericSlot.from, wrapNumericField(committedKey, nextValue))
      .setTextSelection(numericSlot.from + nextValue.length)
      .run();

    // Every OTHER occurrence of the same key, and of any key that means the same
    // measured value. Nikhil's 02 Sep ask: "if something says stress resting, and
    // it is anywhere else, and if you change one of them, it should all change."
    // The resting heart rate is printed in both the ECG sentence and the stress
    // findings; the percentage is printed in the findings and the conclusion. The
    // clinician only ever clicks one of them.
    //
    // The slot just written already holds `nextValue`, so it is skipped by the
    // no-change guard inside `applyNumericUpdates` — this only touches the copies.
    const propagated: Record<string, string> = {};
    numericAliasGroup(committedKey).forEach(key => {
      propagated[key] = nextValue;
    });
    applyNumericUpdates(editor, propagated);

    // Derived stress values. Runs after the typed value is in the document so the
    // derivation and the report can never disagree about what was entered. A
    // cleared field (back to "--") derives nothing and leaves the percentage as
    // it stands, rather than wiping a figure the clinician may still want.
    if (committedKey === PEAK_HR_KEY && trimmed) {
      // No explicit propagation needed: the dispatch fires the editor's own
      // `update` handler, which is what pushes content out for autosave.
      const derived = deriveStressHeartRateValues(trimmed, data);
      if (Object.keys(derived).length > 0) {
        applyNumericUpdates(editor, derived);
      }
    }

    setTimeout(() => {
      editor.view.dom.scrollTop = scrollPosition;
    }, 0);

    setNumericPosition(null);
    setNumericSlot(null);
    setNumericDraft('');
  };

  /**
   * Write a retyped measurement value back, then push it through the chain.
   *
   * This is the behaviour Nikhil asked for on the 25 Aug call: change Height
   * and BSA follows, change BSA and every indexed value follows, however many
   * levels deep the formula goes. The rules are enforced in
   * `measurementChain.ts`; this only moves values between the document and it.
   *
   * The value map is the document's own chained values layered OVER the
   * report's data, so a value the clinician already corrected wins over the
   * one the SR mapping produced when the report was created.
   */
  const handleCommitChainField = () => {
    const role = getRole();
    if (!canEngineWriteToReport(role, reportStatus)) {
      return;
    }
    if (!editor || !chainSlot) {
      return;
    }

    const { key, originalValue, unit, separator } = chainSlot;
    // The clinician typed a figure; the unit and its original separator come back from the
    // slot untouched, so an unedited value rebuilds byte-for-byte and the no-change guard
    // below still recognises it.
    const nextValue = joinValueAndUnit(chainDraft, unit, separator);

    // An empty value would collapse the span and lose the field from the
    // report for good, so a cleared input is treated as "no change".
    if (!nextValue || nextValue === originalValue) {
      setChainPosition(null);
      setChainSlot(null);
      setChainDraft('');
      return;
    }

    const scrollPosition = editor.view.dom.scrollTop;
    const documentValues = collectChainSnapshot(editor);
    const baseValues = { ...data, ...(data?.measurements || {}) };
    // A field such as EF is printed more than once. If the clicked copy is not
    // the final occurrence, the document snapshot can still contain the old
    // value from a later copy. The popup entry is the source of truth for this
    // commit and must win before the update is propagated to every occurrence.
    const currentValues = { ...baseValues, ...documentValues, [key]: nextValue };
    const previousValues = { ...baseValues, ...documentValues, [key]: originalValue };

    const { updates, warnings } = recomputeChain(key, currentValues, previousValues);

    const warningByKey: Record<string, string | null> = {
      // Always written, so correcting a value back into agreement clears an
      // earlier flag instead of leaving it stuck on amber.
      [key]: warnings.find(w => w.key === key)?.message || null,
    };

    // One mark-preserving transaction updates the clicked value and every
    // duplicate. Avoid deleting/reinserting HTML here: doing that first can
    // alter the surrounding table-cell marks and alignment.
    applyChainUpdates(editor, updates, warningByKey);
    // The sentence states the pressure the chain assumed, so a RAP retyped in the table
    // has to reach the dropdown as well.
    if (key === 'RAP') {
      syncRapPhrase(editor, nextValue);
    }
    chainValuesRef.current = collectChainSnapshot(editor);

    if (Object.keys(updates).length > 0 || warnings.length > 0) {
      setChainNotice({
        changed: Object.keys(updates).map(k => CHAIN_FIELDS[k]?.label || k),
        warnings: warnings.map(w => w.message),
      });
    } else {
      setChainNotice(null);
    }

    setTimeout(() => {
      editor.view.dom.scrollTop = scrollPosition;
    }, 0);

    setChainPosition(null);
    setChainSlot(null);
    setChainDraft('');
  };

  /**
   * Commit a dynamic field. Much smaller than `handleCommitChainField` — a
   * dynamic field has no unit, no dependency graph, no per-key warning state.
   * Just rewrite every occurrence of this key to the typed text.
   */
  /**
   * `overrideDraft`, when given, is committed instead of the `dynamicFieldDraft`
   * state — a dropdown pick passes its option straight from the click, because the
   * state set in the same tick is still stale in this closure.
   */
  const handleCommitDynamicField = (overrideDraft?: string) => {
    const role = getRole();
    if (!canEngineWriteToReport(role, reportStatus)) {
      return;
    }
    if (!editor || !dynamicFieldSlot) {
      return;
    }

    const { key, originalValue, fieldType, unit } = dynamicFieldSlot;
    const draft = overrideDraft !== undefined ? overrideDraft : dynamicFieldDraft;
    // A number field's draft is unit-less (see the click handler); reattach the
    // fixed unit before writing, same as chainField's own commit.
    const nextValue = fieldType === 'number' ? joinValueAndUnit(draft, unit) : draft;

    // An empty value would collapse the span and lose the field for good, so a
    // cleared input is treated as "no change", same reasoning as chainField.
    if (!nextValue.trim() || nextValue === originalValue) {
      setDynamicFieldPosition(null);
      setDynamicFieldSlot(null);
      setDynamicFieldDraft('');
      return;
    }

    applyDynamicFieldUpdate(editor, key, nextValue);

    setDynamicFieldPosition(null);
    setDynamicFieldSlot(null);
    setDynamicFieldDraft('');
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.suggestion-dropdown') &&
        !event.target.closest('span[data-editable]')
      ) {
        setDropdownPosition(null);
      }
      if (
        !event.target.closest('.numeric-field-popup') &&
        !event.target.closest('span[data-editable-number]')
      ) {
        setNumericPosition(null);
        setNumericSlot(null);
      }
      if (
        !event.target.closest('.chain-field-popup') &&
        !event.target.closest('span[data-chain-field]')
      ) {
        setChainPosition(null);
        setChainSlot(null);
      }
      if (
        !event.target.closest('.dynamic-field-popup') &&
        !event.target.closest('span[data-dynamic-field]')
      ) {
        setDynamicFieldPosition(null);
        setDynamicFieldSlot(null);
      }
    };
    const handleReportScroll = () => {
      setDropdownPosition(null);
      setNumericPosition(null);
      setNumericSlot(null);
      setChainPosition(null);
      setChainSlot(null);
      setDynamicFieldPosition(null);
      setDynamicFieldSlot(null);
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('scroll', handleReportScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleReportScroll, true);
    };
  }, []);

  // ------------------------------------------------------------------------------------------------------------ //

  const dispatch = useDispatch<AppDispatch>();
  const [createdReportId, setCreatedReportId] = useState<string | null>(
    props?.reportTemplate?.from ? props?.reportTemplate?._id : null
  );

  const params = useParams();
  const currentUrl = window.location.href;
  const currUrl = new URL(currentUrl);
  const queryParams = new URLSearchParams(currUrl.search);
  const studyInstanceUID = queryParams.get('StudyInstanceUIDs') || params?.studyInstanceUIDs;

  /**
   * Is this a stress report? Gates everything stress-specific, so an ECHO report
   * never carries stress values it has no placeholders for.
   */
  const isStressReport = React.useMemo(() => {
    const reportKind =
      props?.reportTemplate?.templateName || props?.reportTemplate?.reportType || '';
    return /stress/i.test(reportKind);
  }, [props?.reportTemplate?.templateName, props?.reportTemplate?.reportType]);

  // Stress reports only: pull the CardioSoft values parsed from the attached XML.
  useEffect(() => {
    if (studyInstanceUID && isStressReport) {
      dispatch(getStressValues({ studyInstanceUID }));
    }
  }, [studyInstanceUID, isStressReport]);

  // Map parsed stress fields → {{stress_*}} placeholder values. Any missing value
  // becomes "--" so the template keeps a visible fill-me marker.
  useEffect(() => {
    if (stressValuesResponse?.status !== 'SUCCESS') return;
    const res: any = stressValuesResponse?.data;
    if (!res?.isStress) return;
    const f = res.fields || {};
    const v = (x: any) => (x !== null && x !== undefined && x !== '' ? String(x) : '--');
    setStressData({
      stress_resting_hr: v(f.restingHR),
      stress_peak_hr: v(f.peakHR),
      // Both blood pressures carry the full systolic/diastolic pair. Reporting a
      // bare systolic loses half of the measurement, and the diastolic response
      // to exercise is itself a finding — a diastolic that climbs rather than
      // holds is abnormal, and CardioSoft prints both.
      stress_resting_bp: v(f.restingBP),
      stress_peak_bp: v(f.peakBP),
      // Legacy placeholder name used by the existing clinic templates; same value
      // as stress_peak_bp. See numericReportFields.ts for why it survives.
      stress_peak_sbp: v(f.peakBP),
      stress_mets: v(f.mets),
      // exerciseTime is "8:30"; exerciseDecimalMinutes is "8.5". Both come from the same
      // minute/second pair — the decimal form reads as a different duration than the test ran.
      stress_exercise_minutes: v(f.exerciseTime),
      stress_target_hr: v(f.targetHR),
      stress_percent_predicted: v(f.percentPredictedHR),
    });
    // Read from the export's narrative by the backend — see `stressArrhythmia.js`.
    setStressArrhythmia((res.meta && res.meta.arrhythmia) || null);
    setStressSource({
      format: String(res.sourceFormat || 'xml').toUpperCase(),
      file: res.source || '',
    });
  }, [stressValuesResponse?.status, stressValuesResponse?.data]);

  useEffect(() => {
    const resolvedId = props?.reportTemplate?.from ? props?.reportTemplate?._id : null;
    setCreatedReportId(resolvedId);
  }, [props?.reportTemplate?._id, props?.reportTemplate?.from]);


  useEffect(() => {
    const data = getSeriesMetadata(studyInstanceUID, seriesInstanceUID);
    props.setSRInstance(data?.instances[0]);
  }, [seriesInstanceUID]);

  // Parsing lives in helper.ts so the measurement workspace reads the SR exactly the
  // same way this does; the report-name and BSA side effects stay here as callbacks.
  const extractContainerData = (container, nodeIndexesTree = [0], containerNumberedTree = [1]) =>
    extractContainerDataShared(container, nodeIndexesTree, containerNumberedTree, {
      onReportName: name => setData(prev => ({ ...prev, report_name: name })),
      onBSA: setBSASR,
    });

  useEffect(() => {
    const SRData = props.SRInstance && <OHIFCornerstoneSRContainer container={props.SRInstance} />;
    setSRInstanceData(SRData);
  }, [props.SRInstance]);

  const convertToHtmlString = () => {
    // Convert the React component to an HTML string
    const html = ReactDOMServer.renderToString(SRInstanceData);
    setHtmlString(html);
  };

  useEffect(() => {
    if (SRInstanceData) {
      convertToHtmlString();
    }
  }, [SRInstanceData]);

  // AF mark for this study. Held per clinic in the same document as the critical
  // and research marks, so one fetch covers all of them.
  useEffect(() => {
    if (!studyInstanceUID) {
      setIsAF(false);
      return;
    }
    dispatch(getStudyMarkingsApi())
      .unwrap()
      .then((res: any) => setIsAF((res?.data?.afList || []).includes(studyInstanceUID)))
      .catch(() => setIsAF(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, studyInstanceUID]);

  useEffect(() => {
    const handleFetchDemographics = async () => {
      const studyData = await fetchPatientDetails(studyInstanceUID);

      const demographics = extractPatientDemographics(studyData);
      setPatientDemographics(demographics);

      const seriesUID = extractSeriesInstanceUID(studyData);
      setSeriesInstanceUID(seriesUID);

      if (props.SRInstance) {
        const container = extractContainerData(props.SRInstance);
        srContentItemsRef.current = container?.contentItems || null;
        setSrReadyTick(t => t + 1);
        const extracted = convertMeasurementsToHTML(
          container,
          demographics.heightRawM,
          demographics.weightRawKg,
          { atrialFibrillation: isAF }
        );
        setMeasurementsHTML(extracted);
        // Resolve BSA from the SAME source as the measurements: SR BSA if present,
        // otherwise calculated from height/weight (SR-embedded, then demographics).
        setBsaCalc(
          resolveReportBSA(
            container?.contentItems,
            demographics.heightRawM,
            demographics.weightRawKg
          )
        );
      } else {
        srContentItemsRef.current = null;
        setSrReadyTick(t => t + 1);
        setMeasurementsHTML(null);
        // No SR — fall back to demographic height/weight so a manual report still
        // gets a calculated BSA when the study tags carry height + weight.
        setBsaCalc(resolveReportBSA(null, demographics.heightRawM, demographics.weightRawKg));
      }
    };
    srConceptCatchAppliedRef.current = false;
    handleFetchDemographics();
    // isAF is a dependency because it changes how EF Biplane resolves — without
    // it, an AF mark that arrives after the first extraction would never reach
    // the printed EF.
  }, [htmlString, isAF]);

  // Heart rate: the DICOM HeartRate tag across the study's images, then the SR
  // concept, then an R-R interval in the SR (resolveReportHeartRate). The tag
  // source is the lowest rate across ALL image series, so while Express is still
  // loading them the row stays empty rather than showing the lowest of a partial
  // set (or the SR's figure, which the tag would replace). The template re-fills
  // when it arrives, unless the clinician has already typed.
  useEffect(() => {
    setBloodPressure(extractBloodPressureFromContainer(srContentItemsRef.current));
    if (props.imageMetadataReady === false) {
      setHeartRate(null);
      setMachineType(null);
      return;
    }
    setHeartRate(resolveReportHeartRate(srContentItemsRef.current, studyInstanceUID));
    setMachineType(getMachineFromStudyTags(studyInstanceUID));
  }, [props.imageMetadataReady, srReadyTick, studyInstanceUID]);

  /**
   * Dynamic-field SR catch (18 Sep) — a dynamic field bound to a raw SR concept name
   * (`data-sr-concept`, see InsertDynamicFieldModal.tsx) gets that value filled in
   * once, here, at report-open. Deliberately separate from the `measurementsHTML`
   * effect above: that one builds the fixed CHAIN_FIELDS set via the alias
   * dictionary; this one is an exact-string catch against a name that is, by
   * definition, NOT in that dictionary. Runs off `editor` + `htmlString` because
   * the template's dynamicField spans only exist in the DOM once the editor has
   * set its content — `srContentItemsRef` alone changing proves nothing about that.
   * `srReadyTick` is the signal that the async SR parse above has actually finished.
   */
  const applySrConceptCatch = () => {
    if (!editor || srConceptCatchAppliedRef.current) {
      return;
    }
    const items = srContentItemsRef.current;
    if (!items) {
      return;
    }
    const spans = editor.view.dom.querySelectorAll<HTMLElement>(
      'span[data-dynamic-field][data-sr-concept]'
    );
    if (spans.length === 0) {
      return;
    }
    srConceptCatchAppliedRef.current = true;
    spans.forEach(span => {
      const key = span.classList[0];
      const concept = span.getAttribute('data-sr-concept');
      const label = span.getAttribute('title') || key;
      const current = span.textContent || '';
      // Never overwrite a value a clinician already typed or a previous catch
      // already applied — only an untouched placeholder is fair game.
      if (!key || !concept || current !== `[${label}]`) {
        return;
      }
      const found = findSRValueByExactConceptName(items, concept);
      if (found) {
        // Filled by the app, not typed: marking it programmatic keeps the report
        // from counting as user-edited, which would stop the measurement table
        // from being filled when the SR values arrive.
        const wasProgrammatic = programmaticSetRef.current;
        programmaticSetRef.current = true;
        try {
          applyDynamicFieldUpdate(editor, key, found);
        } finally {
          programmaticSetRef.current = wasProgrammatic;
        }
      }
    });
  };

  useEffect(() => {
    applySrConceptCatch();
  }, [editor, htmlString, srReadyTick]);

  useEffect(() => {
    // const cleanedMeasurement = Object.fromEntries(
    //   Object.entries(measurementsHTML).map(([key, value]) => [
    //     key,
    //     String(value).replace(/\^/g, ''),
    //   ])
    // );
    // BSA priority: SR value (already formatted) → resolved/calculated BSA →
    // whatever the demographics extractor produced. Guarantees BSA shows on every
    // report, calculated when the SR omits it.
    const resolvedBsa =
      bsaSR || (bsaCalc != null ? `${bsaCalc.toFixed(2)} m²` : patientDemographics.bsa);
    setData(prev => ({
      ...prev,
      ...patientDemographics,
      bsa: resolvedBsa,
      heart_rate: heartRate || '',
      blood_pressure: bloodPressure || '',
      machine_type: machineType || '',
      measurements: measurementsHTML,
    }));
  }, [
    patientDemographics,
    measurementsHTML,
    bsaSR,
    bsaCalc,
    heartRate,
    bloodPressure,
    machineType,
  ]);

  /**
   * Write `name` into the cell that follows a `<label>:` cell in the report body.
   *
   * Parameterised by label because the Cardiologist and Sonographer rows are the same
   * shape and need identical treatment — the cardiologist behaviour is unchanged.
   */
  const injectNameIntoHTML = (html: string, name: string, labelPattern: RegExp): string => {
    if (!html || !name) return html;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const tds = doc.querySelectorAll('td');
      for (let i = 0; i < tds.length - 1; i++) {
        if (labelPattern.test(tds[i].textContent?.trim() || '')) {
          const valueP = tds[i + 1].querySelector('p');
          if (valueP) {
            valueP.textContent = name;
          }
          break;
        }
      }
      return doc.body.querySelector('div').innerHTML;
    } catch {
      return html;
    }
  };

  const extractTextFromNode = (node: any): string => {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    if (Array.isArray(node.content)) return node.content.map(extractTextFromNode).join('');
    return '';
  };

  const injectNameIntoJSON = (
    json: any,
    personName: string,
    labelPattern: RegExp,
    markAttribute: string
  ): any => {
    if (!json || typeof json !== 'object' || !personName) return json;

    const injectInNode = (node: any): any => {
      if (!node || typeof node !== 'object') return node;
      if (Array.isArray(node)) return node.map(injectInNode);

      if (node.type === 'tableRow' && Array.isArray(node.content)) {
        const cells: any[] = node.content;
        const labelIdx = cells.findIndex(cell => {
          const cellText = extractTextFromNode(cell);
          return labelPattern.test(cellText.trim());
        });
        if (labelIdx !== -1 && labelIdx + 1 < cells.length) {
          const updatedCells = [...cells];
          const valueCell = updatedCells[labelIdx + 1];
          updatedCells[labelIdx + 1] = {
            ...valueCell,
            attrs: { ...(valueCell.attrs || {}), [markAttribute]: 'true' },
            content: [
              {
                type: 'paragraph',
                attrs: { class: 'editor-paragraph' },
                content: personName ? [{ type: 'text', text: personName }] : [],
              },
            ],
          };
          return { ...node, content: updatedCells };
        }
      }

      if (node.content) {
        return { ...node, content: Array.isArray(node.content) ? node.content.map(injectInNode) : node.content };
      }
      return node;
    };

    return injectInNode(json);
  };

  const injectCardiologistIntoHTML = (html: string, name: string): string =>
    injectNameIntoHTML(html, name, /cardiologist:/i);
  const injectCardiologistIntoJSON = (json: any, name: string): any =>
    injectNameIntoJSON(json, name, /cardiologist:/i, 'data-assign-cardiologist');

  const injectSonographerIntoHTML = (html: string, name: string): string =>
    injectNameIntoHTML(html, name, /sonographer:/i);

  const populateTemplate = (template, data) => {
    if (!template) return '';

    const loggedInName = sessionStorage.getItem('name') || localStorage.getItem('name') || '';
    const reportDoc = props?.reportTemplate?.data || {};

    // Sonographer = whoever the picker last chose, which is persisted as `createdByName`.
    // Fallback to the logged-in user during initial creation (before it is persisted).
    const sonographerName = props?.sonographer || reportDoc.createdByName || loggedInName || '';
    // Cardiologist: prefer explicit prop (passed from outer component), fallback to doc field.
    const cardiologistName = props?.reportAssignedTo || reportDoc.reportAssignedTo || '';

    const replaceRolePlaceholder = (key, value) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi');
      template = template.replace(regex, value);
    };
    replaceRolePlaceholder('sonographer', sonographerName);
    replaceRolePlaceholder('cardiologist', cardiologistName);

    // Replace all other placeholders using data object.
    // Numeric slots are wrapped in a click-to-edit span rather than emitted as
    // bare text, so a clinician can fill them in place. Without this they were
    // dead text: when no CardioSoft XML is attached (the normal case) every
    // stress number rendered as "--" with no way to correct it short of
    // retyping the sentence.
    return template.replace(/\{\{(.*?)\}\}/g, (_, raw) => {
      const key = raw.trim();
      const value = data[key] || '';
      if (isNumericFieldKey(key)) {
        return wrapNumericField(key, value);
      }
      // Measurement values that participate in the chain are wrapped so they
      // have an identity to update later. Everything else — and any chain
      // field with no value — is emitted as the plain text it has always
      // been. See chainFieldMarkup.ts.
      return wrapChainField(key, value);
    });
  };

  // Tracks the cardiologist name last injected in THIS mount. undefined means
  // "haven't injected yet this mount" — on a fresh mount (e.g. minimize/reopen
  // remount), the loaded templateHTML already has whatever was last saved
  // (including any manual edit to that cell), so the first run must not
  // re-inject over it. Only a real change to reportAssignedTo (an actual
  // reassignment via the Assign modal, happening within this same mount)
  // should overwrite the cell.
  const prevCardiologistNameRef = React.useRef<string | undefined>(undefined);
  /**
   * Highest `assignmentSeq` already written into the report body. Guards
   * against re-injecting the same assignment when the effect re-runs for an
   * unrelated reason (new measurement data, stress values arriving).
   */
  const lastAppliedAssignmentSeqRef = React.useRef<number>(0);

  useEffect(() => {
    // Populate the template with data on component mount.
    //
    // Once the user has typed, repopulating would feed stale content to
    // autosave and lose their edits. Assignment-after-typing is handled by its
    // own effect below, which rewrites the single cell instead of the body.
    if (userDirtyRef.current) {
      return;
    }

    const typeofTemplate = typeof props?.reportTemplate?.templateHTML;
    const cardiologistName = props?.reportAssignedTo || props?.reportTemplate?.data?.reportAssignedTo || '';

    const transformedData = { ...data, ...data?.measurements, ...stressData };

    // A CardioSoft file that carries a peak HR but no percentage still gets one.
    // The parsers only ever read the percentage out of the export
    // (`PercentAchievedMaxPredicted`, or "This value represents NN %"), so a
    // machine or protocol that omits it left the slot at "--" even though every
    // input needed to work it out was on the report. Only fills a gap — an
    // imported percentage is never overwritten. See `stressHeartRate.ts`.
    //
    // Gated on the report kind rather than on the values: the predicted maximum
    // can be derived from age ALONE, so without this gate an ECHO report would
    // pick up a `stress_target_hr` it has no placeholder for. Harmless today
    // (no non-stress template carries a `{{stress_*}}` key) but only by accident.
    if (isStressReport) {
      Object.entries(deriveStressHeartRateValues(transformedData[PEAK_HR_KEY], data)).forEach(
        ([key, value]) => {
          const existing = transformedData[key];
          if (!existing || existing === NUMERIC_EMPTY_MARKER) {
            transformedData[key] = value;
          }
        }
      );
    }

    let populatedContent =
      typeofTemplate === 'string'
        ? populateTemplate(props?.reportTemplate?.templateHTML, transformedData)
        : props?.reportTemplate?.templateHTML;

    /**
     * Did the user just assign someone through the modal?
     *
     * This used to be INFERRED, by comparing the name against the last one
     * latched in a ref. That inference had a hole: the ref latched only a
     * truthy name, so on an unassigned report it stayed `undefined` for the
     * whole mount — and the very first assignment therefore looked like "not a
     * reassignment" and was skipped. The name saved fine on the server and the
     * success message appeared, but the Cardiologist cell in the split-screen
     * report stayed blank until the report was reopened through 3 dots →
     * View Report, which remounts from the raw template and takes the branch
     * below. Reported 18 Aug with a screen recording.
     *
     * `assignmentSeq` is now incremented by the modal's success callback, so
     * this is a fact rather than a guess. It also keeps the property the old
     * inference was protecting: a late-arriving `reportAssignedTo` on mount
     * does not count as an assignment, so a manually edited cell is not
     * clobbered on reload.
     */
    const assignmentSeq = props?.assignmentSeq || 0;
    const isReassignment =
      assignmentSeq > 0 && assignmentSeq !== lastAppliedAssignmentSeqRef.current;

    if (typeofTemplate === 'string' && cardiologistName) {
      // Brand-new report from a raw template — always resolve the placeholder.
      populatedContent = injectCardiologistIntoHTML(populatedContent as string, cardiologistName);
    } else if (typeofTemplate !== 'string' && cardiologistName && isReassignment) {
      // Existing (already-saved) report — only overwrite the cell on a real
      // reassignment, not on every reload of already-saved content.
      populatedContent = injectCardiologistIntoJSON(populatedContent, cardiologistName);
    }

    if (isReassignment) {
      lastAppliedAssignmentSeqRef.current = assignmentSeq;
    }
    if (cardiologistName) {
      prevCardiologistNameRef.current = cardiologistName;
    }

    setContent(populatedContent);
    props?.setUpdatedEditorContent(stripChainMarks(populatedContent));
  }, [
    props?.reportTemplate?.templateHTML,
    props?.reportAssignedTo,
    props?.assignmentSeq,
    data,
    stressData,
    isStressReport,
  ]);

  // Once the user has typed, programmatic re-population must NOT clobber their
  // edits. SR demographics/measurements resolve late and re-run the populate
  // effect above — before this guard, that reset the editor mid-typing and the
  // next autosave persisted the reverted body (user changes silently lost).
  const userDirtyRef = React.useRef(false);
  const programmaticSetRef = React.useRef(false);
  useEffect(() => {
    userDirtyRef.current = false;
  }, [props?.reportTemplate?._id]);

  useEffect(() => {
    if (editor && content) {
      if (userDirtyRef.current) {
        return; // user has edits in the editor — never overwrite them
      }
      // Set content in the editor after it’s populated
      programmaticSetRef.current = true;
      editor.commands.setContent(content);

      /**
       * Mark the chained values NOW, with the body actually in the editor.
       *
       * This used to live in its own effect, which read `editorContent` — state that the
       * line below only SCHEDULES. That effect ran in the same commit as this one, saw
       * null, bailed, and never re-ran, because its dependency was `content` rather than
       * the value it guarded on. Two consequences, and they are the ones Nikhil described
       * on 07 Sep:
       *
       *   - the document arrived unmarked, so nothing was clickable or connected until
       *     the debounced pass caught up 600 ms later ("that connection takes some time
       *     to establish")
       *   - `chainValuesRef` was still empty, so the clinician's FIRST edit was consumed
       *     as the opening snapshot instead of being propagated, and only a second edit
       *     moved anything ("sometimes it's working")
       *
       * `chainWritingRef` keeps these writes out of the update handler, so priming cannot
       * dirty the report or trigger an autosave.
       */
      chainWritingRef.current = true;
      try {
        // `fillEmpty` only where the engine may write: the placeholder is real text, and
        // a Finalized report must not gain characters just by being opened.
        backfillChainMarks(editor, {
          fillEmpty: canEngineWriteToReport(getRole(), reportStatus),
        });
        // The aorta sentence has no editable span, so its slot is made before the
        // wording rules look for it.
        backfillAortaSlot(editor);
        // The tricuspid sentence prints RVSP with no anchor of its own, so it is given
        // one here and joins the chain like any other measurement.
        backfillTricuspidPressure(editor, {
          fillEmpty: canEngineWriteToReport(getRole(), reportStatus),
        });
        chainValuesRef.current = collectChainSnapshot(editor);
      } finally {
        chainWritingRef.current = false;
      }

      programmaticSetRef.current = false;
      setEditorContent(content);
      props?.setUpdatedEditorContent(stripChainMarks(content));

      // The body was just rebuilt from the template, so any SR-linked dynamic field
      // is a placeholder again. Fill them back in (untouched placeholders only).
      srConceptCatchAppliedRef.current = false;
      applySrConceptCatch();
    }
    return () => {
      editor?.commands.clearContent();
    };
  }, [editor, content]);

  /**
   * Assignment made while the user already had unsaved edits.
   *
   * The populate effect above bails out entirely once `userDirtyRef` is set,
   * which is correct for a full rebuild but meant an assignment made after any
   * typing never reached the Cardiologist cell — the second route to the blank
   * cell reported on 18 Aug. This writes that one cell into the editor's
   * CURRENT document rather than re-deriving the body from the template, so
   * nothing the user typed is touched.
   *
   * Marked programmatic so the write does not itself count as a user edit.
   */
  useEffect(() => {
    const seq = props?.assignmentSeq || 0;
    if (!editor || seq === 0 || seq === lastAppliedAssignmentSeqRef.current) {
      return;
    }
    if (!userDirtyRef.current) {
      return; // clean editor — the populate effect already handled it
    }
    const cardiologistName =
      props?.reportAssignedTo || props?.reportTemplate?.data?.reportAssignedTo || '';
    if (!cardiologistName) {
      return;
    }

    const withName = injectCardiologistIntoJSON(editor.getJSON(), cardiologistName);
    programmaticSetRef.current = true;
    editor.commands.setContent(withName);
    programmaticSetRef.current = false;
    setEditorContent(withName);
    props?.setUpdatedEditorContent(stripChainMarks(withName));
    lastAppliedAssignmentSeqRef.current = seq;
  }, [props?.assignmentSeq, props?.reportAssignedTo, editor]);

  /**
   * Write the chosen sonographer into the "Sonographer:" cell.
   *
   * One effect, keyed on the name, because this only ever changes when the picker
   * returns — unlike `reportAssignedTo`, which also arrives on mount and needed a
   * sequence guard to avoid clobbering a manually edited cell.
   */
  useEffect(() => {
    if (!editor || !props?.sonographer) {
      return;
    }
    const html = editor.getHTML();
    const injected = injectSonographerIntoHTML(html, props.sonographer);
    if (injected === html) {
      return;
    }
    programmaticSetRef.current = true;
    editor.commands.setContent(injected);
    programmaticSetRef.current = false;
    setEditorContent(injected);
    props?.setUpdatedEditorContent(stripChainMarks(injected));
  }, [props?.sonographer, editor]);

  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        // Marking values for the chain, or writing recomputed ones, is
        // housekeeping — not a clinician edit. Propagating it would make
        // simply opening a report dirty the document and trigger an autosave
        // that rewrites every stored report body.
        if (chainWritingRef.current) {
          return;
        }
        if (!programmaticSetRef.current) {
          userDirtyRef.current = true;
        }
        const updatedContent = editor.getJSON(); // Get content as JSON
        setEditorContent(updatedContent);
        props?.setUpdatedEditorContent(stripChainMarks(updatedContent));
      });
    }
  }, [editor]);

  /**
   * Smart wording — pick each sentence's phrase from the measurements.
   *
   * Only the rules Dr Shashi has signed off run here (see `wordingRules.ts`); the rest of
   * the proposed set is waiting on his answers and is deliberately absent rather than
   * guessed at.
   *
   * The engine will not put words in a clinician's mouth. A slot is written only while it
   * still holds what the report opened with, or what this engine last wrote into it. The
   * moment a human changes it, the rule stops writing and raises a note instead — the same
   * treatment a mid-chain edit gets.
   *
   * Returns true when the document was changed.
   */
  const runWordingRules = useCallback(
    (activeEditor: any): boolean => {
      if (!activeEditor) {
        return false;
      }

      const phrases = collectPhraseSnapshot(activeEditor);
      const occurrencesBySlot = groupPhraseOccurrences(activeEditor);

      const updates: Record<string, string> = {};
      const notes: string[] = [];
      /**
       * Findings the engine worked out but deliberately did NOT write — today, the
       * diastolic grade and the filling-pressure call. Kept apart from `notes` because
       * they are shown only alongside a change the engine actually made, never on their
       * own; a report that opens with nothing altered must open silently.
       */
      const advisoryNotes: string[] = [];

      /**
       * Write a phrase into a slot — only while the slot is still the engine's to write.
       *
       * Ownership is read from the document itself (`data-user` / `data-auto` on the
       * span), not from a snapshot taken when the report opened. That is the fix for
       * Dr Shashi's complaint of 06 Sep: a baseline snapshot cannot survive a save, so a
       * correction a tech made yesterday came back today looking exactly like a template
       * default and was overwritten. See `isPhraseSlotLocked`.
       *
       * A locked slot is passed over in SILENCE. Once a clinician owns a sentence, the
       * engine disagreeing with it is the normal permanent state of the report, not news
       * — and saying so on every open is what turned the notice into something they had
       * to dismiss all day.
       */
      const propose = (
        slot: string,
        phrase: string | null,
        reason: string,
        advisory = false
      ) => {
        if (!phrase) {
          return; // this clinic's list cannot express it — never invent wording
        }
        const current = phrases[slot];
        if (current === undefined) {
          return; // template does not carry the sentence
        }
        // Case-insensitive, because `applyPhraseUpdates` cases a phrase to suit the
        // position it prints in: the arrhythmia slot reads "no arrhythmias" mid-sentence
        // in the Findings and "No arrhythmias" starting the Conclusions bullet. An exact
        // compare would read the capitalised copy as a clinician's disagreement and
        // re-raise the same note on every pass.
        if (phrase.toLowerCase() === current.toLowerCase()) {
          return; // already says this
        }
        if (isPhraseSlotLocked(occurrencesBySlot[slot] || [])) {
          return; // the clinician's wording stands, and stands quietly
        }
        if (advisory) {
          // Told, not written. The clinician decides whether it belongs in the report.
          advisoryNotes.push(`${reason} suggests "${phrase}" — for review, not applied.`);
          return;
        }
        updates[slot] = phrase;
        notes.push(`${reason} → "${phrase}"`);
      };

      /**
       * Arrhythmia, read from the attached CardioSoft export rather than from the
       * measurements.
       *
       * Nikhil, 02 Sep: the report printed "no arrhythmias" while the CardioSoft
       * report for the same test said "Atrial premature beats" — "we're not
       * detecting the right arrhythmia here in this drop-down". There was no
       * detection at all: the slot had only ever been filled by hand, so every
       * report showed the template's default.
       *
       * This is the one finding here that is imported rather than derived, which is
       * why it does not live in `wordingRules.ts` — those rules take measurements
       * and thresholds. The provenance is stated in the note so a clinician can see
       * the change came from their own machine's report.
       */
      const detected = stressArrhythmiaRef.current;
      if (detected) {
        const phrase = resolveArrhythmiaPhrase(detected, dropdownMapRef.current?.[ARRHYTHMIA_SLOT]);
        propose(
          ARRHYTHMIA_SLOT,
          phrase,
          `CardioSoft report states "${detected.source}"`
        );
        // Findings past the first are not written — the slot holds one phrase —
        // so they are named instead of being silently dropped.
        if (detected.findings && detected.findings.length > 1) {
          notes.push(
            `CardioSoft also recorded ${detected.findings
              .slice(1)
              .map(f => f.replace(/_/g, ' '))
              .join(', ')} — add manually if required.`
          );
        }
      }

      const measurements = { ...data, ...(data?.measurements || {}), ...collectChainSnapshot(activeEditor) };
      const sexInput = data?.sex ?? data?.gender;
      // AF is passed through because the diastolic algorithm assumes sinus rhythm — E/A
      // means nothing without an A wave — so that one rule stays silent in AF.
      const suggestions = evaluateWordingRules(measurements, sexInput, {
        atrialFibrillation: isAF,
      });

      for (const suggestion of suggestions) {
        propose(
          suggestion.slot,
          resolvePhrase(suggestion.intent, dropdownMapRef.current?.[suggestion.slot]),
          `${suggestion.reason}${suggestion.borderline ? ' (borderline)' : ''}`,
          suggestion.advisory === true
        );
      }

      // `true` = these are the engine's own words; stamp `data-auto` so a later open can
      // tell them from a clinician's edit.
      const written =
        Object.keys(updates).length > 0 ? applyPhraseUpdates(activeEditor, updates, true) : 0;

      /**
       * The notice appears only when the engine actually CHANGED the report.
       *
       * Dr Shashi, 06 Sep: *"it's not saving as it's creates a message and we have to
       * dismiss. Rather then saving time it's causing more time consumption"*, and
       * *"Every time we have to dismiss"*. It fired on every open because a mere
       * disagreement raised a note — and once a clinician owns a sentence, disagreement
       * is the report's normal permanent state, so there was always something to say.
       *
       * Now: nothing written, nothing shown. Advisory findings ride along with a real
       * change rather than summoning a notice of their own, so opening a settled report
       * is silent.
       */
      if (written > 0 && (notes.length > 0 || advisoryNotes.length > 0)) {
        setChainNotice(previous => ({
          changed: previous?.changed || [],
          warnings: [...notes, ...advisoryNotes],
        }));
      }

      return written > 0;
    },
    [data, isAF]
  );

  /**
   * Run the chain from whatever the clinician just typed.
   *
   * The 25 Aug call asked for "update something in the top header and
   * everything changes according to that" — in practice that means editing the
   * number in the cell, not opening a popup first. So the chain is driven by
   * the document itself: re-mark anything that came loose, read the current
   * values, and see which single one moved since last time.
   *
   * Only a SINGLE changed value triggers a recompute. A paste, an undo, or a
   * template reload moves many at once, and there is no sensible "source" to
   * chain from — those just resync the snapshot silently rather than fighting
   * the user.
   */
  const runChainFromDocument = useCallback(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    const role = getRole();
    if (!canEngineWriteToReport(role, reportStatus)) {
      return;
    }

    chainWritingRef.current = true;
    try {
      // Re-mark first: retyping over a value can take its mark with it, and an
      // unmarked value is invisible to the chain.
      backfillChainMarks(editor, { fillEmpty: true });
      backfillAortaSlot(editor);
      backfillTricuspidPressure(editor, { fillEmpty: true });

      const previous = chainValuesRef.current;
      if (!previous) {
        chainValuesRef.current = collectChainSnapshot(editor); // first pass, nothing to compare
        // The wording rules read the measurements, not an edit, so they still apply on the
        // very first pass after a report opens.
        //
        // ⚠️ The result MUST be pushed out for autosave, and it was not: this branch
        // called the rules, discarded the return value and returned early, while
        // `chainWritingRef` had the editor's own `update` handler suppressed. So a
        // sentence the engine rewrote when the report opened appeared on screen and never
        // reached the database — the clinician saw one report and the record held another,
        // and the same rewrite happened again on the next open. Dr Shashi, 06 Sep:
        // *"it's not saving"*.
        if (runWordingRules(editor)) {
          const updatedContent = editor.getJSON();
          setEditorContent(updatedContent);
          props?.setUpdatedEditorContent(stripChainMarks(updatedContent));
        }
        return;
      }

      // Occurrence-aware: a field printed in several places (EF is in the measurement
      // table, the Left Ventricle paragraph and the Conclusions) must react to an edit
      // made to ANY of its copies, not just the first one in the document.
      const edited = findEditedChainValue(editor, previous);
      if (!edited) {
        chainValuesRef.current = collectChainSnapshot(editor);
        if (runWordingRules(editor)) {
          const updatedContent = editor.getJSON();
          setEditorContent(updatedContent);
          props?.setUpdatedEditorContent(stripChainMarks(updatedContent));
        }
        return;
      }

      const editedKey = edited.key;
      const baseValues = { ...data, ...(data?.measurements || {}) };
      const { updates, warnings } = recomputeChain(
        editedKey,
        { ...baseValues, ...previous, [editedKey]: edited.value },
        { ...baseValues, ...previous }
      );

      const warningByKey: Record<string, string | null> = {
        // Always written, so correcting a value back into agreement clears an
        // earlier flag instead of leaving it stuck on amber.
        [editedKey]: warnings.find(w => w.key === editedKey)?.message || null,
      };

      applyChainUpdates(editor, updates, warningByKey);
      if (editedKey === 'RAP') {
        syncRapPhrase(editor, String(edited.value));
      }
      chainValuesRef.current = collectChainSnapshot(editor);

      if (Object.keys(updates).length > 0 || warnings.length > 0) {
        setChainNotice({
          changed: Object.keys(updates).map(k => CHAIN_FIELDS[k]?.label || k),
          warnings: warnings.map(w => w.message),
        });
      }

      const wordingChanged = runWordingRules(editor);

      if (Object.keys(updates).length > 0 || wordingChanged) {
        // The chain's own writes DO belong in the saved report — they are the
        // clinician's edit, propagated. Push the result out for autosave.
        const updatedContent = editor.getJSON();
        setEditorContent(updatedContent);
        props?.setUpdatedEditorContent(stripChainMarks(updatedContent));
      }
    } finally {
      chainWritingRef.current = false;
    }
  }, [editor, data, props?.setUpdatedEditorContent, runWordingRules]);

  const debouncedChainContent = useDebounce(editorContent, 600);

  /**
   * Held in a ref so the pass below is triggered by the DOCUMENT and the MEASUREMENTS
   * changing, and by nothing else. `runChainFromDocument` is rebuilt whenever `data` gets
   * a fresh object identity, and depending on the callback made every unrelated re-render
   * run another full pass — a document walk and a re-mark each time, for no change.
   */
  const runChainRef = React.useRef(runChainFromDocument);
  runChainRef.current = runChainFromDocument;

  /**
   * The measurement values the chain and the wording rules actually read, as a string.
   * A string dependency settles: `data.measurements` is a new object on every render even
   * when the numbers are identical, so keying off it re-triggered the pass forever.
   */
  const measurementSignature = React.useMemo(
    () => JSON.stringify({ ...(data?.measurements || {}), sex: data?.sex ?? data?.gender }),
    [data]
  );

  /**
   * How many phrase lists this clinic has loaded. The lists are fetched separately from
   * the report, so on a newly created report they can arrive AFTER the rules have run —
   * and a rule with no vocabulary to match against skips silently and never retries. That
   * looked exactly like "smart wording does nothing on a new report". Counting them means
   * the pass runs again the moment they land, and never again after that.
   */
  const dropdownCount = Object.keys(dropdownMap || {}).length;

  useEffect(() => {
    if (!debouncedChainContent) {
      return;
    }
    runChainRef.current();
    // `stressArrhythmia` is a dependency for the same reason `dropdownCount` is:
    // the stress export is fetched separately from the report, so it can land
    // after the wording pass has already run, and a pass with nothing to read
    // skips silently and never retries.
  }, [debouncedChainContent, measurementSignature, dropdownCount, stressArrhythmia]);

  const buildCreatePayload = useCallback(
    templateHTML => {
      const now = new Date();

      return {
        templateHTML,
        editorMode: '',
        reportName: props?.reportTemplate?.templateName,
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().split(' ')[0],
        studyInstanceUID,
        seriesInstanceUID,
        status: 'Draft',
        comment: 'comment',
        hospitalId: getHospitalId(),
        reportType: props?.reportTemplate?.templateName,
        colorCode: props?.colorCode,
        ...patientDemographics,
        headerDetails: props.headerDetails,
        attachedClips: props?.attachedClips || [],
      };
    },
    [
      patientDemographics,
      props?.colorCode,
      props?.headerDetails,
      props?.reportTemplate?.templateName,
      seriesInstanceUID,
      studyInstanceUID,
      props?.attachedClips,
    ]
  );

  const persistCreate = useCallback(
    async (templateHTML: any, { silent = false }: { silent?: boolean } = {}) => {
      if (!templateHTML || createdReportId) {
        return;
      }

      // `silent` = the automatic draft created on Generate. It must not show the blocking
      // loader or the "Report drafted successfully" modal, because closing that modal
      // navigates to /reports — the clinician has to stay on the report they just opened.
      if (!silent) {
        props?.setIsLoading?.(true);
      }

      try {
        const response = await dispatch(createReportApi(buildCreatePayload(templateHTML))).unwrap();
        const newReportId = response?.result?._id || response?.data?._id || response?._id;

        if (newReportId) {
          setCreatedReportId(newReportId);
          props?.onReportCreated?.(newReportId, templateHTML);
        }

        dispatch(clearCreateState());
        if (!silent) {
          props?.setIsSuccess?.(true);
        }
      } catch {
        // A silent draft has no modal to fail into, and without Save Report there is no
        // retry affordance — surface it on the autosave indicator instead of failing mute.
        props?.onAutoSaveStatusChange?.('error');
        if (!silent) {
          props?.setIsLoading?.(false);
        }
      } finally {
        if (!silent) {
          props?.setIsLoading?.(false);
        }
      }
    },
    [buildCreatePayload, createdReportId, dispatch, props]
  );

  const handleSave = async () => {
    await persistCreate(stripChainMarks(editor?.getJSON()));
  };

  /**
   * Generate Report now drafts the report immediately (agreed with Nik, 05 Aug 2026).
   *
   * Previously nothing existed in the database until "Save Report" was pressed, so a
   * half-finished report was lost if the tab was closed, clips and cardiologist assignment
   * were unavailable (both need a reportId), and saving jumped the user to /reports.
   * Drafting on open removes all of that: autosave owns the body from here, and an
   * unwanted draft can be deleted from the Reports list as before.
   *
   * Opt-in from the parent (`autoDraft`), which is the component that can tell a fresh
   * Generate from an already-saved report being reopened.
   */
  const autoDraftedRef = React.useRef(false);
  useEffect(() => {
    if (
      autoDraftedRef.current ||
      createdReportId ||
      !props?.autoDraft ||
      !studyInstanceUID ||
      !editor ||
      !content ||
      !patientDemographics?.patientName
    ) {
      return;
    }
    autoDraftedRef.current = true;
    persistCreate(stripChainMarks(editor.getJSON()), { silent: true });
  }, [
    content,
    createdReportId,
    editor,
    patientDemographics?.patientName,
    persistCreate,
    props?.autoDraft,
    studyInstanceUID,
  ]);

  useImperativeHandle(ref, () => ({
    triggerChildFunction: handleSave,
  }));

  return (
    <>
      <MenuBar editor={editor} />
      {dropdownPosition && suggestions.length > 0 && (
        <div
          className="suggestion-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            color: 'black',
            zIndex: 1000,
            padding: '3px',
            width: `${SUGGESTION_POPUP_WIDTH}px`,
            maxWidth: 'calc(100% - 24px)',
            boxSizing: 'border-box',
            maxHeight: '200px', // Max height before scrolling
            overflowY: 'auto', // Enables scrolling if content overflows
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleReplaceWord(suggestion)}
              className="suggestion-item"
              style={{
                padding: '4px',
                cursor: 'pointer',
                borderBottom: index !== suggestions.length - 1 ? '1px solid #ddd' : 'none',
                fontSize: '12px',
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      {chainNotice && (
        <div
          style={{
            // Anchored to the viewport's bottom-left rather than sticking to the
            // bottom of the report. Nikhil, 02 Sep: "can you just make it appear
            // on the left here — not blocking the report". As a sticky element it
            // sat over the report text itself, and since a single BSA edit raises
            // it, it covered the very sentences the clinician was checking.
            //
            // Fixed, not sticky, so it cannot be scrolled under the report either,
            // and width-capped so it never reaches the page's centred column.
            position: 'fixed',
            left: '16px',
            bottom: '16px',
            maxWidth: '300px',
            zIndex: 1300,
            margin: 0,
            padding: '8px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0b1f2a',
            boxShadow: '0 2px 10px rgba(0, 16, 31, 0.18)',
            backgroundColor: chainNotice.warnings.length ? '#fef3c7' : '#ccfbf1',
            border: `1px solid ${chainNotice.warnings.length ? '#d19a2a' : '#2dd4bf'}`,
          }}
        >
          {chainNotice.warnings.map(message => (
            <div
              key={message}
              style={{ fontWeight: 600, marginBottom: '4px' }}
            >
              {message}
            </div>
          ))}
          {chainNotice.changed.length > 0 && <div>Updated: {chainNotice.changed.join(', ')}.</div>}
          <div
            onClick={() => setChainNotice(null)}
            style={{
              marginTop: '4px',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </div>
        </div>
      )}
      {chainPosition && chainSlot && (() => {
        // Freeform fields (EF_Prose) have no "half-typed" state — any text is a complete
        // value. `isCompleteNumber` exists only to stop a numeric edit ("4.7" passing
        // through "4" and "4." on the way) from cascading a wrong figure through the
        // chain; a freeform field has no chain to cascade through in that sense.
        const isFreeformChainDraft = !!CHAIN_FIELDS[chainSlot.key]?.freeform;
        const chainDraftComplete = isFreeformChainDraft || isCompleteNumber(chainDraft);
        return (
        <div
          className="chain-field-popup"
          style={{
            position: 'fixed',
            top: chainPosition.top,
            left: chainPosition.left,
            width: `${CHAIN_POPUP_WIDTH}px`,
            maxWidth: 'calc(100% - 32px)',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            color: 'black',
            zIndex: 1000,
            padding: '8px',
          }}
        >
          <div style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}>
            {chainSlot.label}
          </div>
          <div style={{ fontSize: '10px', color: '#8494a8', marginBottom: '4px' }}>
            Values calculated from this one update with it.
          </div>
          {/*
            The unit sits OUTSIDE the input, as fixed text. It is deliberately not
            editable — see `measurementValueParts.ts` for why a mangled unit is worse
            than it looks.
          */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px solid ${
                chainDraft.trim() !== '' && !chainDraftComplete ? '#d19a2a' : '#ccc'
              }`,
              borderRadius: '3px',
              padding: '0 6px',
              background: '#fff',
            }}
          >
            <input
              autoFocus
              value={chainDraft}
              onChange={e => setChainDraft(e.target.value)}
              onBlur={() => {
                if (chainCancelledRef.current) return;
                // Only a finished number is applied — an edit drives the whole chain, so a
                // partial value would cascade a wrong figure. Partial is discarded.
                if (chainDraftComplete) {
                  handleCommitChainField();
                } else {
                  setChainPosition(null);
                  setChainSlot(null);
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Incomplete: hold the popup open and wait for the rest of the number.
                  if (chainDraftComplete) {
                    handleCommitChainField();
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  chainCancelledRef.current = true;
                  setChainPosition(null);
                  setChainSlot(null);
                }
              }}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '4px 0',
                border: 'none',
                outline: 'none',
              }}
            />
            {chainSlot.unit && (
              <span
                style={{
                  fontSize: '12px',
                  color: '#8494a8',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                {chainSlot.unit}
              </span>
            )}
          </div>
          <div
            style={{
              marginTop: '5px',
              fontSize: '10px',
              color:
                chainDraft.trim() !== '' && !chainDraftComplete ? '#b8801a' : '#8494a8',
              minHeight: '13px',
            }}
          >
            {chainDraft.trim() !== '' && !chainDraftComplete
              ? 'Finish the number to apply it.'
              : 'Applied when you click away.'}
          </div>
        </div>
        );
      })()}
      {dynamicFieldPosition &&
        dynamicFieldSlot?.fieldType === 'dropdown' &&
        dynamicFieldSlot.options.length > 0 && (
          <div
            className="suggestion-dropdown dynamic-field-popup"
            style={{
              position: 'fixed',
              top: dynamicFieldPosition.top,
              left: dynamicFieldPosition.left,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              color: 'black',
              zIndex: 1000,
              padding: '3px',
              width: `${SUGGESTION_POPUP_WIDTH}px`,
              maxWidth: 'calc(100% - 24px)',
              boxSizing: 'border-box',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {dynamicFieldSlot.options.map((opt, index) => (
              <div
                key={opt}
                onClick={() => handleCommitDynamicField(opt)}
                className="suggestion-item"
                style={{
                  padding: '4px',
                  cursor: 'pointer',
                  borderBottom:
                    index !== dynamicFieldSlot.options.length - 1 ? '1px solid #ddd' : 'none',
                  fontSize: '12px',
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      {dynamicFieldPosition &&
        dynamicFieldSlot &&
        !(dynamicFieldSlot.fieldType === 'dropdown' && dynamicFieldSlot.options.length > 0) && (
        <div
          className="dynamic-field-popup"
          style={{
            position: 'fixed',
            top: dynamicFieldPosition.top,
            left: dynamicFieldPosition.left,
            width: `${CHAIN_POPUP_WIDTH}px`,
            maxWidth: 'calc(100% - 32px)',
            boxSizing: 'border-box',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            color: 'black',
            zIndex: 1000,
            padding: '8px',
          }}
        >
          <div style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}>
            {dynamicFieldSlot.label}
          </div>
          <div style={{ fontSize: '10px', color: '#8494a8', marginBottom: '4px' }}>
            A field this clinic added — every copy stays in sync.
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              padding: '0 6px',
              background: '#fff',
            }}
          >
            <input
              autoFocus
              inputMode={dynamicFieldSlot.fieldType === 'number' ? 'decimal' : undefined}
              value={dynamicFieldDraft}
              onChange={e => setDynamicFieldDraft(e.target.value)}
              onBlur={() => {
                if (dynamicFieldCancelledRef.current) return;
                handleCommitDynamicField();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCommitDynamicField();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  dynamicFieldCancelledRef.current = true;
                  setDynamicFieldPosition(null);
                  setDynamicFieldSlot(null);
                }
              }}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '4px 0',
                border: 'none',
                outline: 'none',
              }}
            />
            {dynamicFieldSlot.fieldType === 'number' && dynamicFieldSlot.unit && (
              <span style={{ fontSize: '12px', color: '#8494a8' }}>{dynamicFieldSlot.unit}</span>
            )}
          </div>
          <div style={{ marginTop: '5px', fontSize: '10px', color: '#8494a8', minHeight: '13px' }}>
            Applied when you click away.
          </div>
        </div>
      )}
      {numericPosition && numericSlot && (
        <div
          className="numeric-field-popup"
          style={{
            position: 'fixed',
            top: numericPosition.top,
            left: numericPosition.left,
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            color: 'black',
            zIndex: 1000,
            padding: '8px',
            width: `${NUMERIC_POPUP_WIDTH}px`,
            maxWidth: 'calc(100% - 24px)',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: '11px', color: '#5b6b80', marginBottom: '4px', fontWeight: 600 }}>
            {NUMERIC_REPORT_FIELDS[numericSlot.key]?.label || numericSlot.key}
          </div>
          {stressSource && numericDraft.trim() !== '' && (
            <div
              style={{ fontSize: '10px', color: '#8494a8', marginBottom: '4px' }}
              title={stressSource.file}
            >
              from attached {stressSource.format}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              autoFocus
              value={numericDraft}
              placeholder={NUMERIC_REPORT_FIELDS[numericSlot.key]?.placeholder || ''}
              onChange={e => setNumericDraft(e.target.value)}
              onBlur={() => {
                if (numericCancelledRef.current) return;
                // Only a finished entry is applied; a half-typed one is discarded rather
                // than written into the report. Enter also commits an EMPTY value, which
                // restores the "--" marker — deliberate intent, unlike a stray click.
                if (isCompleteNumericEntry(numericDraft)) {
                  handleCommitNumeric();
                } else {
                  setNumericPosition(null);
                  setNumericSlot(null);
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (isCompleteNumericEntry(numericDraft) || numericDraft.trim() === '') {
                    handleCommitNumeric();
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  numericCancelledRef.current = true;
                  setNumericPosition(null);
                  setNumericSlot(null);
                }
              }}
              style={{
                width: '100%',
                fontSize: '13px',
                padding: '4px 6px',
                border: `1px solid ${
                  numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
                    ? '#d19a2a'
                    : '#ccc'
                }`,
                borderRadius: '3px',
                outline: 'none',
              }}
            />
            <span style={{ fontSize: '12px', color: '#5b6b80', whiteSpace: 'nowrap' }}>
              {NUMERIC_REPORT_FIELDS[numericSlot.key]?.unit}
            </span>
          </div>
          <div
            style={{
              marginTop: '5px',
              fontSize: '10px',
              color:
                numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
                  ? '#b8801a'
                  : '#8494a8',
              minHeight: '13px',
            }}
          >
            {numericDraft.trim() !== '' && !isCompleteNumericEntry(numericDraft)
              ? 'Finish the value to apply it.'
              : 'Applied when you click away.'}
          </div>
        </div>
      )}
    </>
  );
});

export default forwardRef<any, any>((props, ref) => {
  const [width, setWidth] = useState(850); // Initial width
  const [SRInstance, setSRInstance] = useState(null);
  const location = useLocation();
  const params = useParams();
  const [data, setData] = useState<any>(props);
  const [updatedEditorContent, setUpdatedEditorContent] = useState({});
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedContent, setLastSavedContent] = useState<any>(null);
  const [lastSavedColorCodes, setLastSavedColorCodes] = useState<any>(null);
  const [isAutoSaveReady, setIsAutoSaveReady] = useState(false);
  const [reportRecordId, setReportRecordId] = useState<string | null>(null);
  const [attachedClips, setAttachedClips] = useState<any[]>([]);
  const [sharedClipsUrl, setSharedClipsUrl] = useState<string | null>(null);
  const [sharedClipsName, setSharedClipsName] = useState<string>('');
  const [isAttachedClipsExpanded, setIsAttachedClipsExpanded] = useState(false);
  const [isAttachClipsOpen, setIsAttachClipsOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [reportAssignedTo, setReportAssignedTo] = useState<string>('');
  const [isSonographerModalOpen, setIsSonographerModalOpen] = useState(false);
  /** Sonographer of record, as shown in the report body's "Sonographer:" row. */
  const [sonographer, setSonographer] = useState<string>('');
  /**
   * Counter bumped on each successful assignment through the modal. The editor
   * uses a CHANGE in this value as the trigger to write the cardiologist into
   * an already-saved report body — previously that was inferred from the name
   * changing, which silently missed the first assignment on an unassigned
   * report (18 Aug bug report).
   */
  const [assignmentSeq, setAssignmentSeq] = useState(0);
  const [restColorCodes, setRestColorCodes] = useState({
    first: '#b7eb8f',
    second: '#b7eb8f',
    third: '#b7eb8f',
    fourth: '#b7eb8f',
    fifth: '#b7eb8f',
    sixth: '#b7eb8f',
    seventh: '#b7eb8f',
    eighth: '#b7eb8f',
    ninth: '#b7eb8f',
    tenth: '#b7eb8f',
    eleventh: '#b7eb8f',
    twelfth: '#b7eb8f',
    thirteenth: '#b7eb8f',
    fourteenth: '#b7eb8f',
    fifteenth: '#b7eb8f',
    sixteenth: '#b7eb8f',
    seventeenth: '#b7eb8f',
  });
  const [stressColorCodes, setStressColorCodes] = useState({
    first: '#b7eb8f',
    second: '#b7eb8f',
    third: '#b7eb8f',
    fourth: '#b7eb8f',
    fifth: '#b7eb8f',
    sixth: '#b7eb8f',
    seventh: '#b7eb8f',
    eighth: '#b7eb8f',
    ninth: '#b7eb8f',
    tenth: '#b7eb8f',
    eleventh: '#b7eb8f',
    twelfth: '#b7eb8f',
    thirteenth: '#b7eb8f',
    fourteenth: '#b7eb8f',
    fifteenth: '#b7eb8f',
    sixteenth: '#b7eb8f',
    seventeenth: '#b7eb8f',
  });

  const headerDetails = data?.hospitalHeader;
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const { loading: updateReportLoading } = useSelector(
    (state: RootState) => state.updateReportResponse
  );

  const isViewerStudyInstance = location.pathname.includes('/viewer');
  const resolvedStudyInstanceUID = (() => {
    try {
      const currUrl = new URL(window.location.href);
      const queryParams = new URLSearchParams(currUrl.search);
      return queryParams.get('StudyInstanceUIDs') || (params as any)?.studyInstanceUIDs || null;
    } catch {
      return (params as any)?.studyInstanceUIDs || null;
    }
  })();

  // `undefined` = the lookup has not answered yet, `null` = this study has no report.
  // Generate now drafts automatically, so opening an already-reported study would silently
  // create a second, empty report next to the real one. Nothing may be drafted until this
  // resolves.
  const [existingStudyReportId, setExistingStudyReportId] = useState<string | null | undefined>(
    undefined
  );

  useEffect(() => {
    if (props?.reportTemplate?.from) {
      setExistingStudyReportId(null); // opened from a saved report — nothing to draft
      return;
    }
    const studyUID =
      resolvedStudyInstanceUID ||
      props?.reportTemplate?.studyInstanceUID ||
      data?.item?.studyInstanceUID ||
      data?.studyInstanceUID;
    if (!studyUID) {
      return;
    }
    let cancelled = false;
    setExistingStudyReportId(undefined);
    dispatch(getCurrentReportApi({ studyInstanceUID: studyUID, hospitalId: getHospitalId() }))
      .unwrap()
      .then((res: any) => {
        if (!cancelled) {
          setExistingStudyReportId(res?.data?._id || null);
        }
      })
      .catch(() => {
        // Treat a failed lookup as "no report" — the create endpoint refuses a duplicate
        // with 409, so a network blip cannot produce a second report.
        if (!cancelled) {
          setExistingStudyReportId(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    resolvedStudyInstanceUID,
    props?.reportTemplate?.from,
    props?.reportTemplate?.studyInstanceUID,
  ]);

  const showTransientResult = useCallback((message: string, isSuccess: boolean) => {
    setDisplayMessage(message);
    if (isSuccess) {
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
      }, 1500);
      return;
    }

    setIsFailedModalOpen(true);
    setTimeout(() => {
      setIsFailedModalOpen(false);
    }, 1500);
  }, []);

  const STRESS_ECHO_TYPES = ['Stress Echo', 'Dobutamine stress echo'];
  const isStressEcho =
    STRESS_ECHO_TYPES.includes(data?.item?.templateName) ||
    STRESS_ECHO_TYPES.includes(data?.item?.reportType);

  const captureAndSaveDiagram = useCallback((reportId: string) => {
    const element = document.querySelector('.report-diagrams');
    if (!element || !reportId) return;
    saveDiagramHtml(reportId, captureDiagramHtml(element));
  }, []);

  const persistUpdate = useCallback(
    async ({
      templateHTML,
      restCodes = restColorCodes,
      stressCodes = stressColorCodes,
      manual = false,
    }: {
      templateHTML: any;
      restCodes?: any;
      stressCodes?: any;
      manual?: boolean;
    }) => {
      if (!templateHTML || !reportRecordId) {
        return;
      }

      if (!manual) {
        setAutoSaveStatus('saving');
      }

      try {
        const response = await dispatch(
          // attachedClips is deliberately NOT sent: no caller passes an `attachedClips` prop
          // into this layout, so this posted an empty array on every save/autosave and the
          // backend (updateReportTemplateHtml treats [] as "set to none") wiped the report's
          // attached clips. Clips are owned by the clips endpoint and the create payload.
          updateReport({
            reportId: reportRecordId,
            templateHTML,
            colorCode: {
              restColorCodes: { ...restCodes },
              stressColorCodes: { ...stressCodes },
            },
          })
        ).unwrap();

        setLastSavedContent(templateHTML);
        setLastSavedColorCodes({ restColorCodes: { ...restCodes }, stressColorCodes: { ...stressCodes } });
        dispatch(clearUpdateState());

        if (isStressEcho && reportRecordId) {
          setTimeout(() => captureAndSaveDiagram(reportRecordId), 200);
        }

        if (manual) {
          showTransientResult(response?.msg || 'Report updated successfully.', true);
        } else {
          setAutoSaveStatus('saved');
          setTimeout(() => {
            setAutoSaveStatus('idle');
          }, 2000);
        }
      } catch (error: any) {
        const message = error?.msg || error?.message || error || 'Failed to save report.';
        setDisplayMessage(message);

        if (manual) {
          showTransientResult(message, false);
        } else {
          setAutoSaveStatus('error');
        }
      } finally {
        if (manual) {
          props?.setIsLoading?.(false);
        }
      }
    },
    [
      captureAndSaveDiagram,
      dispatch,
      isStressEcho,
      reportRecordId,
      restColorCodes,
      showTransientResult,
      stressColorCodes,
    ]
  );

  const handleReportCreated = useCallback(
    (newReportId: string, savedContent: any) => {
      setReportRecordId(newReportId);
      setLastSavedContent(savedContent);
      setIsAutoSaveReady(true);

      // Tell the surrounding chrome a report now exists for this study. Express decides
      // "View Report + report icons" vs "Generate Report" from the fetched current report
      // (DetailsTile.resolvedReportId), which was resolved BEFORE this draft was created — so
      // without this refetch the header keeps offering Generate and hides the attach-clips
      // and distribution actions until the page is reloaded.
      const studyUID =
        resolvedStudyInstanceUID ||
        props?.reportTemplate?.studyInstanceUID ||
        data?.item?.studyInstanceUID ||
        data?.studyInstanceUID;
      if (studyUID) {
        dispatch(getCurrentReportApi({ studyInstanceUID: studyUID, hospitalId: getHospitalId() }));
      }
    },
    [data, dispatch, props?.reportTemplate?.studyInstanceUID, resolvedStudyInstanceUID]
  );

  const handleAutoSaveStatusChange = useCallback(
    (status: 'idle' | 'saving' | 'saved' | 'error') => {
      setAutoSaveStatus(status);
    },
    []
  );

  const handleMouseDown = e => {
    const startX = e.clientX;
    const startWidth = width;

    const onMouseMove = e => {
      let newWidth = startWidth - (e.clientX - startX);
      newWidth = Math.max(300, Math.min(900, newWidth)); // Keep width between 300px and 900px

      setWidth(newWidth);
      props?.setRigthWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (props?.setRightHeight) {
      props?.setRigthWidth(width);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(props?.reportTemplate || {}).length > 0) {
      setData(props?.reportTemplate);

      // Guard: only restore color codes before the auto-save cycle is ready (initial load).
      // After isAutoSaveReady=true, color codes are owned by local state — restoring from
      // stale props after a Redux dispatch (clearUpdateState) would overwrite unsaved edits.
      if (props?.reportTemplate?.from && !isAutoSaveReady) {
        const restCodes = props?.reportTemplate?.colorCode?.restColorCodes;
        const stressCodes = props?.reportTemplate?.colorCode?.stressColorCodes;
        if (restCodes && Object.keys(restCodes).length > 0) {
          setRestColorCodes(restCodes);
        }
        if (stressCodes && Object.keys(stressCodes).length > 0) {
          setStressColorCodes(stressCodes);
        }
      }
    }
    setAttachedClips(props?.reportTemplate?.attachedClips || []);
    setSharedClipsUrl(props?.reportTemplate?.sharedClipsUrl || null);
    setSharedClipsName(props?.reportTemplate?.sharedClipsName || '');
    setReportAssignedTo(props?.reportTemplate?.reportAssignedTo || '');
  }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Keep small lists open by default; collapse long lists to avoid consuming report space.
    setIsAttachedClipsExpanded(attachedClips.length > 0 && attachedClips.length <= 8);
  }, [attachedClips.length]);

  /**
   * Attach the study's clips the moment a report exists, so the clinician never has to
   * open the modal just to press Save (Nikhil, 1 Sep). Gated on `canEditMedicalData`, so
   * a Finalized report is never written to. Runs once per report, and never on one that
   * has had clips saved before — see the hook.
   *
   * NOT gated on `autoSaveStatus`. That was tried and removed on 1 Sep: it made `enabled`
   * flip within ~2s of the report populating, which re-ran the hook's effect mid-flight
   * and cancelled the attach outright. Narrowing the server-side write is the right fix
   * for the read-modify-replace overlap; churning this dependency is not.
   */
  useAutoAttachClips({
    reportId: reportRecordId,
    studyInstanceUID:
      resolvedStudyInstanceUID || data?.item?.studyInstanceUID || data?.studyInstanceUID,
    attachedClips,
    sharedClipsUrl,
    enabled:
      props?.isUpdateable !== false &&
      canEditMedicalData(getRole(), data?.status || data?.reportTemplate?.data?.status),
    onAttached: (clips, shareUrl, name) => {
      setAttachedClips(clips);
      if (shareUrl) setSharedClipsUrl(shareUrl);
      if (name) setSharedClipsName(name);
    },
  });

  /**
   * Adopt the report id carried by the template props.
   *
   * This used to reset the id to null on every change of `reportTemplate`, which
   * is why Attach Clips and Assign Cardiologist intermittently refused to work
   * (reported 18 Aug). On a brand-new report the sequence is: draft is created →
   * `handleReportCreated` sets the id → the surrounding chrome refetches and
   * hands down a fresh `reportTemplate`. If that replacement arrived without a
   * truthy `from` — which it does while the refetch is still in flight — this
   * effect wiped the id we had just obtained, and both buttons fell back to
   * "Save the report once before attaching clips". Trying again worked only
   * because the refetch had landed by then, which is exactly the "sometimes"
   * in the report.
   *
   * Now the id is only ever cleared when the report genuinely changes identity
   * (a different `_id`), never merely because the incoming props lack one.
   */
  const lastTemplateIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    const templateId = props?.reportTemplate?._id || null;
    const incomingRecordId = props?.reportTemplate?.from ? templateId : null;
    const isDifferentReport =
      lastTemplateIdRef.current !== null &&
      templateId !== null &&
      templateId !== lastTemplateIdRef.current;

    if (incomingRecordId) {
      setReportRecordId(incomingRecordId);
    } else if (isDifferentReport) {
      setReportRecordId(null);
    }

    // Autosave baselines still reset on any template swap: the content changed
    // even when the identity did not, and a stale baseline would mark real
    // edits as already-saved.
    if (templateId !== lastTemplateIdRef.current) {
      setLastSavedContent(null);
      setIsAutoSaveReady(false);
      setAutoSaveStatus('idle');
    }

    if (templateId !== null) {
      lastTemplateIdRef.current = templateId;
    }
  }, [props?.reportTemplate?._id, props?.reportTemplate?.from]);

  useEffect(() => {
    if (!reportRecordId || !isStressEcho) return;
    const timer = setTimeout(() => captureAndSaveDiagram(reportRecordId), 800);
    return () => clearTimeout(timer);
  }, [reportRecordId, isStressEcho, captureAndSaveDiagram]);


  const debouncedContent = useDebounce(updatedEditorContent, 2000);
  const debouncedRestColorCodes = useDebounce(restColorCodes, 2000);
  const debouncedStressColorCodes = useDebounce(stressColorCodes, 2000);

  // Baseline = the hydrated (loaded) content, captured IMMEDIATELY. The old
  // debounced priming meant an edit made within the first 2s window became the
  // baseline itself — marked "already saved" and silently never persisted.
  useEffect(() => {
    if (isAutoSaveReady || !reportRecordId) return;
    if (!updatedEditorContent || Object.keys(updatedEditorContent).length === 0) return;
    // A regenerated body replaces what is stored, so it has no saved baseline:
    // autosave must write it even if the user never types.
    if (props?.reportTemplate?.from === 'regenerate') {
      setIsAutoSaveReady(true);
      return;
    }
    setLastSavedContent(updatedEditorContent);
    setLastSavedColorCodes({ restColorCodes: { ...restColorCodes }, stressColorCodes: { ...stressColorCodes } });
    setIsAutoSaveReady(true);
  }, [updatedEditorContent, isAutoSaveReady, reportRecordId]);

  // Flush any pending (sub-debounce) edit on unmount — closing the report panel
  // or navigating away used to drop up to 2s of typing.
  const autoSaveFlushRef = React.useRef<any>({});
  useEffect(() => {
    autoSaveFlushRef.current = {
      id: reportRecordId,
      ready: isAutoSaveReady,
      content: updatedEditorContent,
      lastSaved: lastSavedContent,
      colors: { restColorCodes: { ...restColorCodes }, stressColorCodes: { ...stressColorCodes } },
    };
  });
  // An unsaved pending edit (sub-debounce typing), or null when everything
  // already persisted. Shared by the unmount backstop and the awaitable flush.
  const getPendingFlush = () => {
    const f = autoSaveFlushRef.current;
    if (!f.id || !f.ready || !f.content || Object.keys(f.content).length === 0) return null;
    if (JSON.stringify(f.content) === JSON.stringify(f.lastSaved)) return null;
    return f;
  };

  // Fire-and-forget backstop: persist pending typing if the panel unmounts
  // through any path that didn't await flushPendingChanges below.
  useEffect(
    () => () => {
      const f = getPendingFlush();
      if (f) {
        dispatch(updateReport({ reportId: f.id, templateHTML: f.content, colorCode: f.colors }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Awaitable flush for callers that close/reopen this panel (express
  // minimize) — they must await this BEFORE unmounting so a subsequent
  // refetch can never read the DB ahead of the write. Returns the editor's
  // current body (for in-memory template sync) or null when unusable.
  // triggerChildFunction keeps its old contract by delegating to the inner
  // editor component.
  const innerRef = React.useRef<any>(null);
  useImperativeHandle(ref, () => ({
    triggerChildFunction: (...args: any[]) => innerRef.current?.triggerChildFunction?.(...args),
    flushPendingChanges: async () => {
      const pending = getPendingFlush();
      if (pending) {
        try {
          await dispatch(
            updateReport({
              reportId: pending.id,
              templateHTML: pending.content,
              colorCode: pending.colors,
            })
          ).unwrap();
          setLastSavedContent(pending.content);
          setLastSavedColorCodes(pending.colors);
        } catch {
          // leave lastSaved untouched — the unmount backstop will retry
        }
      }
      const f = autoSaveFlushRef.current;
      return f.content && Object.keys(f.content).length > 0 ? f.content : null;
    },
  }));

  useEffect(() => {
    if (!reportRecordId || !debouncedContent || Object.keys(debouncedContent).length === 0) {
      return;
    }

    if (!isAutoSaveReady) {
      return;
    }

    if (autoSaveStatus === 'saving') {
      return;
    }

    const contentUnchanged = JSON.stringify(debouncedContent) === JSON.stringify(lastSavedContent);
    const colorCodesUnchanged =
      JSON.stringify({ restColorCodes: debouncedRestColorCodes, stressColorCodes: debouncedStressColorCodes }) ===
      JSON.stringify(lastSavedColorCodes);

    if (contentUnchanged && colorCodesUnchanged) {
      return;
    }

    void persistUpdate({
      templateHTML: debouncedContent,
      restCodes: debouncedRestColorCodes,
      stressCodes: debouncedStressColorCodes,
    });
  }, [
    autoSaveStatus,
    debouncedContent,
    debouncedRestColorCodes,
    debouncedStressColorCodes,
    isAutoSaveReady,
    lastSavedContent,
    lastSavedColorCodes,
    persistUpdate,
    reportRecordId,
  ]);

  const AutoSaveStatus: React.FC<{ status: 'idle' | 'saving' | 'saved' | 'error' }> = ({
    status,
  }) => {
    if (status === 'idle') return null;

    return (
      <div className="auto-save-status text-xs text-gray-600">
        {status === 'saving' && 'Saving...'}
        {status === 'saved' && 'All changes saved'}
        {status === 'error' && 'Auto-save failed'}
      </div>
    );
  };

  return (
    <>
      <div
        className="report-layout border-primary-light spaceAroundHeading relative mt-[2px] box-border overflow-y-scroll rounded border-2 bg-white p-6"
        style={
          isViewerStudyInstance ? { width: `${width}px`, maxWidth: '900px', minWidth: '300px' } : {}
        }
        ref={props?.containerRef}
      >
        <div
          className="resize-handle hover:bg-primary absolute top-0 left-0 z-10"
          style={{ height: '150dvh' }}
          onMouseDown={handleMouseDown}
        ></div>
        <div className="flex items-center justify-between">
          <div
            className=""
            style={{ height: '150px', width: '200px', overflow: 'content' }}
          >
            <img
              src={data?.hospitalLogo}
              alt="Hospital Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="text-normal text-right text-black">
            <p style={{ fontSize: '16px', fontWeight: '600' }}>{headerDetails?.hospitalName}</p>
            <p>
              {headerDetails?.address} - {headerDetails?.city}, {headerDetails?.zipCode}
            </p>
            <p>
              <span>
                <strong>Phone No : </strong>({headerDetails?.contactNumberCountryCode}){' '}
                {headerDetails?.contactNumber}{', '}
              </span>
              {headerDetails?.fax && (
                <span>
                  <strong>Fax : </strong>
                  {headerDetails?.fax}
                </span>
              )}
            </p>
            {headerDetails?.email && (
              <p>
                <strong>Email : </strong> {headerDetails?.email}
              </p>
            )}
            {headerDetails?.website && (
              <p>
                <strong>Website : </strong>{' '}
                <a
                  href={headerDetails?.website}
                  className="text-primary"
                  target="_blank"
                  rel="noreferrer"
                >
                  {headerDetails?.website}
                </a>
              </p>
            )}
          </div>
        </div>
        <div style={(() => {
          // If isUpdateable is explicitly passed, check it
          if (props?.isUpdateable !== undefined && props?.isUpdateable !== null && !props.isUpdateable) {
            return { pointerEvents: 'none' as const, opacity: 0.8, userSelect: 'none' as const };
          }
          const role = getRole();
          // Non-doctor, non-sonographer roles (admin, receptionist, practice manager) can never edit
          if (!isDoctorRole(role) && !canEditMedicalData(role, data?.status || data?.reportTemplate?.data?.status)) {
            return { pointerEvents: 'none' as const, opacity: 0.8, userSelect: 'none' as const };
          }
          let blocked = false;
          if (isDoctorRole(role)) {
            const userId = getUserId();
            const assignedToId = data?.reportAssignedToId || data?.reportTemplate?.data?.reportAssignedToId;
            if (assignedToId) {
              if (String(assignedToId) !== String(userId)) blocked = true;
            }
            if (!blocked) {
              const status = data?.status || data?.reportTemplate?.data?.status;
              if (status) blocked = !canEditMedicalData(role, status);
            }
          }
          return blocked ? { pointerEvents: 'none' as const, opacity: 0.8, userSelect: 'none' as const } : {};
        })()}>
          <EditorProvider
            editorProps={{
              attributes: { class: 'editor-container', 'data-gramm': 'false', 'data-gramm_editor': 'false', 'data-enable-grammarly': 'false' },
            }}
            slotBefore={
              <ReportLayout
                reportTemplate={data?.item}
                autoDraft={
                  !!data?.item?.templateName &&
                  !props?.reportTemplate?.from &&
                  !reportRecordId &&
                  existingStudyReportId === null
                }
                headerDetails={headerDetails}
                ref={innerRef}
                setIsLoading={props?.setIsLoading}
                setIsSuccess={props?.setIsSuccess}
                setSRInstance={setSRInstance}
                SRInstance={SRInstance}
                imageMetadataReady={props?.imageMetadataReady}
                setUpdatedEditorContent={setUpdatedEditorContent}
                onReportCreated={handleReportCreated}
                onAutoSaveStatusChange={handleAutoSaveStatusChange}
                attachedClips={attachedClips}
                colorCode={{
                  restColorCodes: { ...restColorCodes },
                  stressColorCodes: { ...stressColorCodes },
                }}
                reportAssignedTo={reportAssignedTo}
                assignmentSeq={assignmentSeq}
                onAssignCardiologist={reportRecordId ? () => setIsAssignModalOpen(true) : undefined}
                sonographer={sonographer}
                onSelectSonographer={
                  reportRecordId ? () => setIsSonographerModalOpen(true) : undefined
                }
              />
            }
            extensions={extensions as any}
          />
        </div>
        {(data?.item?.templateName == 'Stress Echo' ||
          data?.item?.reportType == 'Stress Echo' ||
          data?.item?.templateName == 'Dobutamine stress echo' ||
          data?.item?.reportType == 'Dobutamine stress echo') && (
          <div
            className="report-diagrams"
            style={{ maxWidth: `${width}px` }}
          >
            <div className="target">
              <RestBullsEyePlot restColorCodes={restColorCodes} />
              <StressBullsEyePlot stressColorCodes={stressColorCodes} />
              <TargetCodes />
            </div>
            <div className="rest-heart-chambers">
              <p className="title title--h5 title--semibold">Rest</p>
              <div className="rest-heart-chambers__grid">
                <div className="item">
                  <Apical2Ch
                    setRestColorCodes={setRestColorCodes}
                    restColorCodes={restColorCodes}
                  />
                </div>
                <div className="item">
                  <Apical3Ch
                    setRestColorCodes={setRestColorCodes}
                    restColorCodes={restColorCodes}
                  />
                </div>
                <div className="item">
                  <Apical4Ch
                    setRestColorCodes={setRestColorCodes}
                    restColorCodes={restColorCodes}
                  />
                </div>
              </div>
            </div>
            <div className="rest-heart-chambers">
              <p className="title title--h5 title--semibold">Stress</p>
              <div className="rest-heart-chambers__grid">
                <div className="item">
                  <StressApical2Ch
                    stressColorCodes={stressColorCodes}
                    setStressColorCodes={setStressColorCodes}
                  />
                </div>
                <div className="item">
                  <StressApical3Ch
                    stressColorCodes={stressColorCodes}
                    setStressColorCodes={setStressColorCodes}
                  />
                </div>
                <div className="item">
                  <StressApical4Ch
                    stressColorCodes={stressColorCodes}
                    setStressColorCodes={setStressColorCodes}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* "Save Report" removed (agreed with Nik, 05 Aug 2026): Generate Report drafts the
            report immediately and autosave owns it from then on, so an explicit save step
            only created the state where clips and doctor assignment were unavailable. The
            autosave indicator below is now the single save signal. */}
        <div className="mt-2">
          <AutoSaveStatus status={autoSaveStatus} />
        </div>

        <div className="mt-3 flex items-center gap-3">
          <Button
            onClick={() => setIsAttachClipsOpen(true)}
            disabled={!reportRecordId}
          >
            Change Clips
          </Button>
          {!reportRecordId ? <span className="text-xs text-gray-500">Save report first to change clips.</span> : null}
        </div>

        {attachedClips.length > 0 ? (
          <div className="attached-clips-section">
            <div className="attached-clips-section__header">
              <h4>Clips Attached - {attachedClips.length}</h4>
            </div>
            {sharedClipsUrl ? (
              <div className="attached-clips-section__share">
                <a
                  href={sharedClipsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attached-clips-section__share-link"
                >
                  See Clips
                </a>
                <button
                  type="button"
                  className="attached-clips-section__copy-btn"
                  onClick={() => navigator.clipboard.writeText(sharedClipsUrl)}
                >
                  Copy Link
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

      </div>

      {/* Loader only for manual saves; autosave uses AutoSaveStatus indicator */}

      <Success
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />

      <Failed
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />

      <AttachClipsModal
        open={isAttachClipsOpen}
        reportId={reportRecordId}
        studyInstanceUID={resolvedStudyInstanceUID || data?.item?.studyInstanceUID || data?.studyInstanceUID}
        initialAttachedClips={attachedClips}
        onClose={() => setIsAttachClipsOpen(false)}
        onSaved={(clips, shareUrl, name) => { setAttachedClips(clips); if (shareUrl) setSharedClipsUrl(shareUrl); setSharedClipsName(name || ''); }}
        initialSharedClipsName={sharedClipsName}
      />

      {isSonographerModalOpen && reportRecordId && (
        <AssignSonographerModal
          isOpen={isSonographerModalOpen}
          reportId={reportRecordId}
          onClose={() => setIsSonographerModalOpen(false)}
          onSelected={name => {
            setSonographer(name);
            setIsSonographerModalOpen(false);
          }}
        />
      )}
      <AssignDoctorModal
        isAssignDoctorModalOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        reportId={reportRecordId || ''}
        AssignReportStatus={reportAssignedTo ? 'Reassign' : 'Assign'}
        setOnAssigningReport={() => {}}
        onAssignSuccess={doctorName => {
          setReportAssignedTo(doctorName);
          // Bump even when the same cardiologist is re-selected: the user
          // performed an assignment, and the report body may not carry the
          // name yet even though the state already does.
          setAssignmentSeq(n => n + 1);
          setIsAssignModalOpen(false);
        }}
      />

    </>
  );
});
